import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { createPortal } from "react-dom";
import ReactMarkdown from "react-markdown";
import remarkBreaks from "remark-breaks";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { useLocation, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Check, CircleAlert, ExternalLink, LoaderCircle, MessageSquareText, Mic2, Plus, ShieldCheck, Trash2, UserRoundPlus } from "lucide-react";
import { getLocaleFromPathname, setSavedLocale, withLocale } from "../utils/locale";
import BusinessFooter from "../components/BusinessFooter";
import DideokAssistant from "../components/DideokAssistant";
import ShopTest from "./ShopTest";
import { ShopProductFrame } from "../components/ShopProductDecoration";
import PartyBotWelcomeSettings from "../components/PartyBotWelcomeSettings";

/* =========================================
   ✅ Kakao AdFit 설정 (좌/우 서로 다른 광고단위)
   ========================================= */
const ADFIT_LEFT_UNIT = "DAN-DLuy6a6LLvSnloJ5";      // 기존(왼쪽)
const ADFIT_RIGHT_UNIT = "DAN-QarXPDehphJPLWiW";      // 신규(오른쪽)

const ADFIT_WIDTH = 160;
const ADFIT_HEIGHT = 600;

const SORT_BY_OPTIONS = {
  ko: [
    { value: "reloaded_at", label: "최근 갱신순", description: "최근 업데이트 기준" },
    { value: "member_count", label: "인원 순위", description: "멤버 수 기준" },
  ],
  vi: [
    { value: "reloaded_at", label: "Mới cập nhật", description: "Ưu tiên server cập nhật gần đây" },
    { value: "member_count", label: "Đông thành viên", description: "Ưu tiên server đông người" },
  ],
};

const SORT_ORDER_OPTIONS = {
  ko: [
    { value: "DESC", label: "내림차순", description: "높은 값부터 정렬" },
    { value: "ASC", label: "오름차순", description: "낮은 값부터 정렬" },
  ],
  vi: [
    { value: "DESC", label: "Giảm dần", description: "Giá trị lớn trước" },
    { value: "ASC", label: "Tăng dần", description: "Giá trị nhỏ trước" },
  ],
};

const COUNTRY_OPTIONS = {
  ko: [
    { value: "ko", label: "한국", flagUrl: "https://flagcdn.com/w20/kr.png" },
    { value: "vi", label: "베트남", flagUrl: "https://flagcdn.com/w20/vn.png" },
  ],
  vi: [
    { value: "ko", label: "Hàn Quốc", flagUrl: "https://flagcdn.com/w20/kr.png" },
    { value: "vi", label: "Việt Nam", flagUrl: "https://flagcdn.com/w20/vn.png" },
  ],
};

const DESCRIPTION_SEARCH_PREFETCH_LIMIT = 20;

function SortDropdown({ value, options, onChange, ariaLabel }) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const prefersReducedMotion = useReducedMotion();
  const selectedOption = options.find((option) => option.value === value) || options[0];

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === "Escape") setIsOpen(false);
    };

    document.addEventListener("mousedown", handleOutsideClick);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
      document.removeEventListener("keydown", handleEscape);
    };
  }, []);

  const handleSelect = (nextValue) => {
    onChange(nextValue);
    setIsOpen(false);
  };

  return (
    <div ref={dropdownRef} className="relative w-full md:w-auto">
      <button
        type="button"
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        onClick={() => setIsOpen((prev) => !prev)}
        className="w-full md:min-w-[128px] bg-[#1e1e1e] border border-[#333] text-white rounded-xl px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#5865F2] transition-colors hover:border-[#4b4e55] flex items-center justify-between gap-2"
      >
        <span className="truncate text-sm font-semibold">{selectedOption?.label}</span>
        <svg
          className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? "rotate-180" : ""}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            initial={prefersReducedMotion ? false : { opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: prefersReducedMotion ? 0 : -4 }}
            transition={{ duration: prefersReducedMotion ? 0 : 0.14, ease: "easeOut" }}
            className="absolute left-0 top-full mt-2 z-40 min-w-[220px] origin-top overflow-hidden rounded-2xl border border-[#333] bg-[#1e1e1e] shadow-[0_18px_35px_rgba(0,0,0,0.45)] transform-gpu"
          >
            {options.map((option) => {
              const isSelected = option.value === value;
              return (
                <motion.button
                  key={option.value}
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  whileTap={prefersReducedMotion ? undefined : { scale: 0.98 }}
                  onClick={() => handleSelect(option.value)}
                  className={`w-full px-4 py-3 text-left transition-colors ${
                    isSelected ? "bg-[#5865F2]/10" : "hover:bg-[#2b2c31]"
                  }`}
                >
                  <div className={`text-base font-bold ${isSelected ? "text-white" : "text-gray-100"}`}>{option.label}</div>
                  {option.description && (
                    <div className={`text-sm ${isSelected ? "text-gray-300" : "text-gray-400"}`}>{option.description}</div>
                  )}
                </motion.button>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function FormDropdown({ value, options, onChange, placeholder, ariaLabel }) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const prefersReducedMotion = useReducedMotion();
  const selectedOption = options.find((option) => option.value === value);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === "Escape") setIsOpen(false);
    };

    document.addEventListener("mousedown", handleOutsideClick);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
      document.removeEventListener("keydown", handleEscape);
    };
  }, []);

  const handleSelect = (nextValue) => {
    onChange(nextValue);
    setIsOpen(false);
  };

  return (
    <div ref={dropdownRef} className="relative w-full">
      <button
        type="button"
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        onClick={() => setIsOpen((prev) => !prev)}
        className="w-full bg-[#2b2c31] border border-[#404249] text-white rounded-lg px-4 py-3 outline-none cursor-pointer hover:border-[#5865F2] focus:ring-2 focus:ring-[#5865F2] transition-colors flex items-center justify-between gap-2"
      >
        <span className={`truncate text-sm ${selectedOption ? "text-white" : "text-gray-400"}`}>{selectedOption?.label || placeholder}</span>
        <svg
          className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? "rotate-180" : ""}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            initial={prefersReducedMotion ? false : { opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: prefersReducedMotion ? 0 : -4 }}
            transition={{ duration: prefersReducedMotion ? 0 : 0.14, ease: "easeOut" }}
            className="absolute left-0 top-full mt-2 z-30 w-full max-h-64 origin-top overflow-y-auto rounded-xl border border-[#404249] bg-[#1e1e1e] shadow-[0_18px_35px_rgba(0,0,0,0.45)] transform-gpu"
          >
            {options.map((option) => {
              const isSelected = option.value === value;
              return (
                <motion.button
                  key={option.value || "__empty__"}
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  whileTap={prefersReducedMotion ? undefined : { scale: 0.98 }}
                  onClick={() => handleSelect(option.value)}
                  className={`w-full px-4 py-2.5 text-left text-sm transition-colors ${
                    isSelected ? "bg-[#5865F2]/10 text-white" : "text-gray-300 hover:bg-[#2b2c31]"
                  }`}
                >
                  {option.label}
                </motion.button>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function CountryDropdown({ value, options, onChange, ariaLabel }) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const prefersReducedMotion = useReducedMotion();
  const selectedOption = options.find((option) => option.value === value) || options[0];

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === "Escape") setIsOpen(false);
    };

    document.addEventListener("mousedown", handleOutsideClick);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
      document.removeEventListener("keydown", handleEscape);
    };
  }, []);

  const handleSelect = (nextLocale) => {
    onChange(nextLocale);
    setIsOpen(false);
  };

  return (
    <div ref={dropdownRef} className="relative">
      <button
        type="button"
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        onClick={() => setIsOpen((prev) => !prev)}
        className="min-w-[156px] bg-[#1e1e1e] border border-[#333] text-white rounded-xl px-3.5 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#5865F2] transition-colors hover:border-[#4b4e55] flex items-center justify-between gap-2"
      >
        <span className="flex items-center gap-2 text-sm font-semibold">
          <img src={selectedOption.flagUrl} alt="" className="w-5 h-4 rounded-sm object-cover" />
          <span>{selectedOption.label}</span>
        </span>
        <svg
          className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? "rotate-180" : ""}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            initial={prefersReducedMotion ? false : { opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: prefersReducedMotion ? 0 : -4 }}
            transition={{ duration: prefersReducedMotion ? 0 : 0.14, ease: "easeOut" }}
            className="absolute right-0 top-full mt-2 z-40 min-w-[190px] origin-top overflow-hidden rounded-2xl border border-[#333] bg-[#1e1e1e] shadow-[0_18px_35px_rgba(0,0,0,0.45)] transform-gpu"
          >
            {options.map((option) => {
              const isSelected = option.value === value;
              return (
                <motion.button
                  key={option.value}
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  whileTap={prefersReducedMotion ? undefined : { scale: 0.98 }}
                  onClick={() => handleSelect(option.value)}
                  className={`w-full px-4 py-3 text-left transition-colors ${
                    isSelected ? "bg-[#5865F2]/10" : "hover:bg-[#2b2c31]"
                  }`}
                >
                  <span className="flex items-center gap-2 text-sm font-semibold text-gray-100">
                    <img src={option.flagUrl} alt="" className="w-5 h-4 rounded-sm object-cover" />
                    <span>{option.label}</span>
                  </span>
                </motion.button>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function AdFitBanner({ unit, width = ADFIT_WIDTH, height = ADFIT_HEIGHT }) {
  return (
    <ins
      className="kakao_ad_area"
      style={{ display: "none" }}
      data-ad-unit={unit}
      data-ad-width={String(width)}
      data-ad-height={String(height)}
    />
  );
}

function getGuildIcon(server, className) {
  const name = server.server_name;
  const cacheBuster = server.reloaded_at ? new Date(server.reloaded_at).getTime() : "default";

  if (server.icon) {
    const extension = server.icon.startsWith("a_") ? "gif" : "png";
    return (
      <img
        src={`https://cdn.discordapp.com/icons/${server.server_id}/${server.icon}.${extension}?t=${cacheBuster}`}
        alt={name || "Server Icon"}
        className={className}
      />
    );
  }

  const initials = (name || "?")
    .split(" ")
    .map((word) => word[0])
    .join("")
    .substring(0, 3);

  return (
    <div className={`flex items-center justify-center bg-gray-700 text-white font-bold ${className}`}>
      {initials}
    </div>
  );
}

function timeAgo(dateString, locale = "ko") {
  const date = new Date(dateString);
  const now = new Date();
  const diff = Math.floor((now - date) / 1000);
  const minutes = Math.floor(diff / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (locale === "vi") {
    if (days > 0) return `${days} ngày trước`;
    if (hours > 0) return `${hours} giờ trước`;
    if (minutes > 0) return `${minutes} phút trước`;
    return "vừa xong";
  }

  if (days > 0) return `${days}일 전`;
  if (hours > 0) return `${hours}시간 전`;
  if (minutes > 0) return `${minutes}분 전`;
  return "방금 전";
}

function CommentItem({ comment, onLike, onReply, onDeleteReply, locale = "ko" }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [replyText, setReplyText] = useState("");
  const [showReplyInput, setShowReplyInput] = useState(false);
  const isVi = locale === "vi";
  const tx = (ko, vi) => (isVi ? vi : ko);

  const DISPLAY_CHAR_LIMIT = 150;
  const content = comment.content || "";
  const isTooLong = content.length > DISPLAY_CHAR_LIMIT;
  const contentToShow = isTooLong && !isExpanded ? content.slice(0, DISPLAY_CHAR_LIMIT) + "..." : content;

  const handleReplySubmit = () => {
    if (!replyText.trim()) return;
    onReply(comment.id, replyText);
    setShowReplyInput(false);
    setReplyText("");
  };

  const handleEditClick = () => {
    setReplyText(comment.reply);
    setShowReplyInput(true);
  };

  const handleDeleteClick = () => {
    if (window.confirm(tx("답글을 삭제하시겠습니까?", "Bạn có chắc muốn xóa trả lời này?"))) {
      onDeleteReply(comment.id);
    }
  };

  return (
    <div className="bg-[#2b2c31] p-4 rounded-xl border border-[#404249] mb-3">
      <div className="flex justify-between items-center mb-2">
        <div className="flex items-center gap-2">
          <span className="text-sm font-bold text-gray-300">{comment.nickname || tx("익명", "Ẩn danh")}</span>
        </div>
        <span className="text-[11px] text-gray-500">{comment.created_at}</span>
      </div>

      <p className="text-gray-200 text-sm whitespace-pre-wrap break-words leading-relaxed mb-3">{contentToShow}</p>
      {isTooLong && (
        <button onClick={() => setIsExpanded(!isExpanded)} className="text-xs text-gray-400 hover:text-white font-bold mb-3">
          {isExpanded ? tx("간략히", "Thu gọn") : tx("자세히 보기", "Xem thêm")}
        </button>
      )}

      <div className="flex items-center justify-between pt-2 border-t border-gray-700/50">
        <button
          onClick={() => onLike(comment.id)}
          className={`flex items-center gap-1.5 text-xs font-medium transition-colors ${
            comment.is_liked ? "text-pink-400" : "text-gray-400 hover:text-gray-200"
          }`}
        >
          <svg className="w-4 h-4" fill={comment.is_liked ? "currentColor" : "none"} stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
            />
          </svg>
          {comment.like_count > 0 ? comment.like_count : tx("좋아요", "Thích")}
        </button>

        {comment.is_owner && !comment.reply && !showReplyInput && (
          <button onClick={() => setShowReplyInput(true)} className="text-xs text-gray-400 hover:text-[#5865F2]">
            {tx("답글 달기", "Trả lời")}
          </button>
        )}
      </div>

      {showReplyInput && (
        <div className="mt-3 animate-fade-in-up">
          <div className="flex gap-2">
            <input
              type="text"
              value={replyText}
              onChange={(e) => setReplyText(e.target.value)}
              placeholder={tx("답글 입력...", "Nhập trả lời...")}
              className="flex-1 bg-[#1e1e1e] text-white text-xs p-2 rounded border border-[#404249] focus:border-[#5865F2] outline-none"
            />
            <button onClick={handleReplySubmit} className="bg-[#5865F2] text-white text-xs px-3 py-1 rounded hover:bg-[#4a56d1]">
              {tx("등록", "Đăng")}
            </button>
          </div>
          <div className="text-right mt-1">
            <button onClick={() => setShowReplyInput(false)} className="text-[10px] text-gray-500 hover:text-gray-300">
              {tx("취소", "Hủy")}
            </button>
          </div>
        </div>
      )}

      {comment.reply && !showReplyInput && (
        <div className="mt-3 bg-[#1e1e1e] p-3 rounded-lg border border-[#5865F2]/30 flex gap-3 relative group">
          <div className="flex-shrink-0 mt-0.5">
            <div className="w-5 h-5 rounded-full bg-[#5865F2] flex items-center justify-center text-white">
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          </div>
          <div className="flex-1">
            <div className="flex items-center justify-between mb-1">
              <span className="text-[#5865F2] text-xs font-bold bg-[#5865F2]/10 px-1.5 rounded">{tx("서버 업로더", "Người đăng server")}</span>
              {comment.is_owner && (
                <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button onClick={handleEditClick} className="text-[10px] text-gray-500 hover:text-white">
                    {tx("수정", "Sửa")}
                  </button>
                  <button onClick={handleDeleteClick} className="text-[10px] text-red-500 hover:text-red-400">
                    {tx("삭제", "Xóa")}
                  </button>
                </div>
              )}
            </div>
            <p className="text-gray-300 text-xs whitespace-pre-wrap leading-relaxed">{comment.reply}</p>
          </div>
        </div>
      )}
    </div>
  );
}

const ServerCard = React.memo(function ServerCard({ server, onOpenDetails, onOpenComments, onTagClick, locale = "ko" }) {
  const isVi = locale === "vi";
  const tx = (ko, vi) => (isVi ? vi : ko);
  const tags = server.tags ? server.tags.split(",").filter((tag) => tag.trim().length > 0) : [];

  const bannerUrl = server.banner_image ? `https://disparty.com/uploads/${server.banner_image}?t=${new Date(server.reloaded_at).getTime()}` : null;
  const shopProduct = server.shopProduct || server.shop_product || null;

  return (
    <ShopProductFrame
      product={shopProduct}
      className={`bg-[#1e1e1e] rounded-2xl overflow-hidden shadow-lg border border-[#333] relative transition-transform duration-150 ease-out transform-gpu hover:-translate-y-0.5 flex flex-col font-${
        server.font || "default"
      }`}
    >
      <div
        className="h-40 w-full bg-center bg-cover relative"
        style={{
          backgroundImage: bannerUrl ? `url(${bannerUrl})` : "none",
          backgroundColor: bannerUrl ? "transparent" : "#2a2a2a",
        }}
      >
        <a
          href={server.invite_url || "#"}
          target="_blank"
          rel="noopener noreferrer"
          aria-disabled={!server.invite_url}
          onClick={(event) => {
            if (!server.invite_url) event.preventDefault();
          }}
          className={`absolute top-3 right-3 z-40 px-4 py-1.5 text-sm font-semibold rounded-md shadow-md text-white transition-colors ${
            server.invite_url ? "bg-[#5865F2] hover:bg-[#4a56d1]" : "bg-gray-700 text-gray-300 cursor-not-allowed"
          }`}
        >
          {tx("서버 참가", "Tham gia")}
        </a>
      </div>

      <div className="p-5 relative flex flex-col flex-1">
        <div className="absolute -top-10 left-6">{getGuildIcon(server, "w-20 h-20 rounded-full border-4 border-[#1e1e1e] object-cover")}</div>

        <div className="mt-10 flex-1 flex flex-col">
          <h2 className="text-xl font-bold text-white mb-2 flex items-center">
            <span className="break-all">{server.server_name}</span>

            {server.is_nsfw && (
              <span className="ml-2 flex-shrink-0 px-2.5 py-0.5 bg-red-500/20 border border-red-500 text-red-400 text-xs font-semibold rounded-full">
                18+
              </span>
            )}
          </h2>

          <div className="flex flex-wrap gap-2 mb-3">
            {tags.map((tag, index) => (
              <button
                key={index}
                onClick={(e) => {
                  e.stopPropagation();
                  if (onTagClick) onTagClick(tag.trim());
                }}
                className="bg-[#121212] border border-[#333] text-[#b5b5b5] text-xs font-medium px-3 py-1 rounded-full whitespace-nowrap hover:border-[#5865F2] hover:text-[#5865F2] transition-colors cursor-pointer"
              >
                {tag}
              </button>
            ))}
          </div>

          <div
            className="prose prose-invert max-w-none bg-[#252525] border border-[#333] rounded-lg p-3 text-sm text-gray-300 overflow-hidden"
            style={{ wordBreak: "break-word", fontSize: "0.92rem", maxHeight: "160px" }}
          >
            <ReactMarkdown
              remarkPlugins={[remarkBreaks]}
              components={{
                p: ({ node, ...props }) => <p className="my-1 leading-snug" {...props} />,
                a: ({ node, ...props }) => <a className="text-blue-400 hover:underline" {...props} />,
                ul: ({ node, ...props }) => <ul className="list-disc pl-5 my-1" {...props} />,
                ol: ({ node, ...props }) => <ol className="list-decimal pl-5 my-1" {...props} />,
                li: ({ node, ...props }) => <li className="my-0.5 leading-snug" {...props} />,
              }}
            >
              {server.description || tx("서버 설명이 없습니다.", "Không có mô tả server.")}
            </ReactMarkdown>
          </div>

          <div className="mt-3 flex items-center justify-between">
            <div className="flex items-center gap-3 text-xs text-gray-500">
              {server.member_count !== undefined && (
                <div className="flex items-center">
                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                    />
                  </svg>
                  {server.member_count.toLocaleString()}
                  {tx("명", " người")}
                </div>
              )}
              <p>{server.reloaded_at ? timeAgo(server.reloaded_at, locale) : ""}</p>
            </div>

            <div className="relative z-40 flex items-center gap-2">
              {server.show_comments && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    if (onOpenComments) onOpenComments(server);
                  }}
                  className="text-sm bg-[#2b2c31] text-gray-300 border border-[#404249] px-3 py-1.5 rounded-md hover:bg-[#36373d] hover:text-white transition flex items-center gap-1"
                  title={tx("익명 댓글", "Bình luận ẩn danh")}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                    />
                  </svg>
                  <span className="hidden xs:inline">{tx("댓글", "Bình luận")}</span>
                </button>
              )}

              <button onClick={() => onOpenDetails(server)} className="relative z-40 text-sm bg-[#5865F2] text-white px-3 py-1.5 rounded-md hover:bg-[#4a56d1] transition">
                {tx("상세보기", "Chi tiết")}
              </button>
            </div>
          </div>
        </div>
      </div>
    </ShopProductFrame>
  );
});

export default function Feed() {
  const localPreview = window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1";
  const location = useLocation();
  const navigate = useNavigate();
  const locale = getLocaleFromPathname(location.pathname);
  const isVi = locale === "vi";
  const tx = useCallback((ko, vi) => (isVi ? vi : ko), [isVi]);
  const prefersReducedMotion = useReducedMotion();
  const sortByOptions = SORT_BY_OPTIONS[locale] || SORT_BY_OPTIONS.ko;
  const sortOrderOptions = SORT_ORDER_OPTIONS[locale] || SORT_ORDER_OPTIONS.ko;
  const countryOptions = COUNTRY_OPTIONS[locale] || COUNTRY_OPTIONS.ko;

  const [servers, setServers] = useState([]);
  const [shopProducts, setShopProducts] = useState([]);
  const [selectedServer, setSelectedServer] = useState(null);
  const [loading, setLoading] = useState(true);

  const [activeTab, setActiveTab] = useState(() => {
    const requestedTab = new URLSearchParams(location.search).get("tab");
    if (requestedTab === "partybot") return "partybot";
    return !isVi && requestedTab === "shop" ? "shop" : "servers";
  });
  const [tabDirection, setTabDirection] = useState(1);
  const isElectron = typeof navigator !== "undefined" && /Electron/i.test(navigator.userAgent);

  const changeActiveTab = useCallback((nextTab) => {
    if (nextTab === activeTab) return;
    const tabOrder = isVi ? ["servers", "partybot"] : ["servers", "shop", "partybot"];
    setTabDirection(tabOrder.indexOf(nextTab) > tabOrder.indexOf(activeTab) ? 1 : -1);
    setActiveTab(nextTab);
  }, [activeTab, isVi]);

  const [isCommentModalOpen, setIsCommentModalOpen] = useState(false);
  const [selectedServerForComments, setSelectedServerForComments] = useState(null);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [loadingComments, setLoadingComments] = useState(false);

  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [isSearchPanelOpen, setIsSearchPanelOpen] = useState(false);
  const [sortBy, setSortBy] = useState("reloaded_at");
  const [sortOrder, setSortOrder] = useState("DESC");
  const [tags, setTags] = useState([]);
  const [appliedTagFilters, setAppliedTagFilters] = useState([]);
  const [draftTagFilters, setDraftTagFilters] = useState([]);
  const [tagSuggestions, setTagSuggestions] = useState([]);
  const [isTagSearch, setIsTagSearch] = useState(false);

  const [showNSFW, setShowNSFW] = useState(false);

  const searchInputRef = useRef(null);
  const observerTarget = useRef(null);
  const descriptionSearchPrefetchRef = useRef(0);
  const [commentSort, setCommentSort] = useState("newest");

  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userInfo, setUserInfo] = useState(null);
  const [loadingAuth, setLoadingAuth] = useState(true);

  useEffect(() => {
    setSavedLocale(locale);
  }, [locale]);

  useEffect(() => {
    if (isVi && activeTab === "shop") {
      setActiveTab("servers");
    }
  }, [activeTab, isVi]);

  useEffect(() => {
    if (isElectron) return undefined;

    // AdFit scans all ad units when this script executes. Keeping this loader
    // tied to the Feed lifetime prevents infinite scroll from replacing ads.
    const script = document.createElement("script");
    script.src = "https://t1.daumcdn.net/kas/static/ba.min.js";
    script.async = true;
    script.dataset.dispartyAdfit = "true";
    document.head.appendChild(script);

    return () => {
      script.remove();
    };
  }, [isElectron]);

  const popularTags = useMemo(
    () => [...tags].sort((a, b) => (Number(b?.count) || 0) - (Number(a?.count) || 0)).slice(0, 20),
    [tags]
  );

  const filteredServers = useMemo(() => {
    const normalizedTerm = debouncedSearchTerm.trim().toLowerCase();
    const isTextSearch = normalizedTerm.length > 0 && !normalizedTerm.startsWith("#");

    return servers.filter((server) => {
      const serverLocale = String(server.country_locale || server.locale || "").toLowerCase();
      if (locale === "vi" && serverLocale && serverLocale !== "vi") {
        return false;
      }
      if (locale === "ko" && serverLocale && serverLocale !== "ko") {
        return false;
      }

      if (isTextSearch) {
        const serverName = (server.server_name || "").toLowerCase();
        const serverDescription = (server.description || "").toLowerCase();

        if (!serverName.includes(normalizedTerm) && !serverDescription.includes(normalizedTerm)) {
          return false;
        }
      }

      if (appliedTagFilters.length > 0) {
        const serverTagSet = new Set(
          (server.tags || "")
            .split(",")
            .map((tag) => tag.trim().toLowerCase())
            .filter((tag) => tag.length > 0)
        );

        for (const tag of appliedTagFilters) {
          if (!serverTagSet.has(String(tag).toLowerCase())) {
            return false;
          }
        }
      }

      return true;
    });
  }, [servers, appliedTagFilters, debouncedSearchTerm, locale]);

  const shopProductById = useMemo(() => {
    const next = {};
    shopProducts.forEach((product) => {
      if (product?.id) next[product.id] = product;
    });
    return next;
  }, [shopProducts]);

  const withShopProduct = useCallback(
    (server) => {
      const product = shopProductById[server?.shop_background_product_id];
      return product ? { ...server, shopProduct: product } : server;
    },
    [shopProductById]
  );

  const decoratedServers = useMemo(
    () => filteredServers.map(withShopProduct),
    [filteredServers, withShopProduct]
  );

  const isServerListRefreshing = loading && filteredServers.length > 0;
  const showServerListLoader =
    (loading && filteredServers.length === 0) ||
    (loadingMore && filteredServers.length === 0 && debouncedSearchTerm.trim() && !debouncedSearchTerm.startsWith("#"));

  const hasActiveSearchFilters =
    Boolean(debouncedSearchTerm.trim()) ||
    appliedTagFilters.length > 0;

  const searchSummaryText = useMemo(() => {
    const parts = [];

    if (debouncedSearchTerm.trim()) {
      parts.push(debouncedSearchTerm.trim());
    }

    if (appliedTagFilters.length > 0) {
      const preview = appliedTagFilters.slice(0, 2).map((tag) => `#${tag}`).join(" ");
      const remain = appliedTagFilters.length - 2;
      parts.push(remain > 0 ? `${preview} +${remain}` : preview);
    }

    return parts.length > 0 ? parts.join(" / ") : tx("찾고 싶은 서버를 입력하세요...", "Nhập server bạn muốn tìm...");
  }, [debouncedSearchTerm, appliedTagFilters, tx]);

  const handleTagClick = useCallback((tagName) => {
    const newSearchTerm = `#${tagName}`;
    setSearchTerm(newSearchTerm);
    setDebouncedSearchTerm(newSearchTerm);
    setAppliedTagFilters([]);
    setDraftTagFilters([]);
    setIsTagSearch(false);
    setTagSuggestions([]);

    setSelectedServer(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const fetchComments = useCallback((serverId, sort = "newest") => {
    setLoadingComments(true);
    fetch(`https://disparty.com/api/servers/${serverId}/comments?sort=${sort}`, { credentials: "include" })
      .then((res) => res.json())
      .then((data) => {
        setComments(data || []);
      })
      .catch((err) => console.error("Failed to fetch comments:", err))
      .finally(() => setLoadingComments(false));
  }, []);

  const handleLike = (commentId) => {
    fetch(`https://disparty.com/api/comments/${commentId}/like`, { method: "POST", credentials: "include" })
      .then((res) => res.json())
      .then(() => {
        setComments((prev) =>
          prev.map((c) => {
            if (c.id === commentId) {
              const diff = c.is_liked ? -1 : 1;
              return { ...c, like_count: c.like_count + diff, is_liked: !c.is_liked };
            }
            return c;
          })
        );
      });
  };

  const handleReply = (commentId, text) => {
    fetch(`https://disparty.com/api/comments/${commentId}/reply`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ reply: text }),
      credentials: "include",
    }).then((res) => {
      if (res.ok) fetchComments(selectedServerForComments.server_id, commentSort);
      else alert(tx("권한이 없거나 오류가 발생했습니다.", "Bạn không có quyền hoặc đã xảy ra lỗi."));
    });
  };

  const handleDeleteReply = (commentId) => {
    fetch(`https://disparty.com/api/comments/${commentId}/reply`, { method: "DELETE", credentials: "include" }).then((res) => {
      if (res.ok) fetchComments(selectedServerForComments.server_id, commentSort);
      else alert(tx("삭제 실패 (권한이 없거나 오류 발생)", "Xóa thất bại (không có quyền hoặc lỗi)."));
    });
  };

  const openCommentModal = useCallback((server) => {
    setSelectedServerForComments(server);
    setIsCommentModalOpen(true);
    setCommentSort("newest");
    fetchComments(server.server_id, "newest");
  }, [fetchComments]);

  const openServerDetails = useCallback((server) => {
    setSelectedServer(server);
  }, []);

  const handlePostComment = () => {
    if (!newComment.trim()) return alert(tx("내용을 입력해주세요.", "Vui lòng nhập nội dung."));
    if (newComment.length > 500) return alert(tx("500자 이내로 입력해주세요.", "Tối đa 500 ký tự."));

    fetch(`https://disparty.com/api/servers/${selectedServerForComments.server_id}/comments`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ content: newComment }),
    })
      .then((res) => {
        if (res.ok) {
          setNewComment("");
          fetchComments(selectedServerForComments.server_id);
        } else {
          res.json().then((err) => alert(err.message || tx("오류가 발생했습니다.", "Đã xảy ra lỗi.")));
        }
      })
      .catch(() => alert(tx("서버 오류가 발생했습니다.", "Lỗi máy chủ.")));
  };

  const checkAuth = useCallback(() => {
    fetch(`https://disparty.com/me?t=${Date.now()}`, {
      credentials: "include",
      method: "GET",
      headers: { "Cache-Control": "no-cache", Pragma: "no-cache" },
    })
      .then((res) => {
        if (res.ok) return res.json();
        throw new Error("Not logged in");
      })
      .then((data) => {
        if (data && data.id) {
          setIsLoggedIn(true);
          setUserInfo(data);
        } else {
          setIsLoggedIn(false);
          setUserInfo(null);
        }
      })
      .catch(() => {
        setIsLoggedIn(false);
        setUserInfo(null);
      })
      .finally(() => setLoadingAuth(false));
  }, []);

  useEffect(() => {
    checkAuth();

    const onFocus = () => checkAuth();
    window.addEventListener("focus", onFocus);

    const timer = setTimeout(checkAuth, 2000);

    return () => {
      window.removeEventListener("focus", onFocus);
      clearTimeout(timer);
    };
  }, [checkAuth]);

  useEffect(() => {
    fetch("https://disparty.com/api/shop/products", { credentials: "include" })
      .then((res) => res.ok ? res.json() : Promise.reject(res))
      .then((data) => setShopProducts(Array.isArray(data) ? data : []))
      .catch(() => setShopProducts([]));
  }, []);

  const handleLoginRedirect = () => {
    window.location.href = withLocale("/profile", locale);
  };

  const handleLocaleChange = (nextLocale) => {
    if (nextLocale === locale) return;
    setSavedLocale(nextLocale);
    navigate(withLocale("/", nextLocale));
  };

  const openSearchPanel = () => {
    setSearchTerm(debouncedSearchTerm);
    setDraftTagFilters(appliedTagFilters);
    setIsTagSearch(false);
    setTagSuggestions([]);
    setIsSearchPanelOpen(true);
  };

  const closeSearchPanel = () => {
    setIsSearchPanelOpen(false);
    setIsTagSearch(false);
    setTagSuggestions([]);
  };

  const toggleDraftTagFilter = (tagName) => {
    setDraftTagFilters((prev) => {
      if (prev.includes(tagName)) {
        return prev.filter((tag) => tag !== tagName);
      }
      return [...prev, tagName];
    });
  };

  const resetSearchDraft = () => {
    setSearchTerm("");
    setDraftTagFilters([]);
    setIsTagSearch(false);
    setTagSuggestions([]);
  };

  const applySearchFilters = () => {
    setDebouncedSearchTerm(searchTerm.trim());
    setAppliedTagFilters(draftTagFilters);
    closeSearchPanel();
  };

  useEffect(() => {
    if (!isSearchPanelOpen) return;

    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const timer = setTimeout(() => {
      if (searchInputRef.current) {
        searchInputRef.current.focus();
      }
    }, prefersReducedMotion ? 0 : 180);

    return () => {
      clearTimeout(timer);
      document.body.style.overflow = originalOverflow;
    };
  }, [isSearchPanelOpen, prefersReducedMotion]);

  useEffect(() => {
    setPage(1);
    descriptionSearchPrefetchRef.current = 0;
  }, [debouncedSearchTerm, sortBy, sortOrder, showNSFW, appliedTagFilters, locale]);

  useEffect(() => {
    if (page === 1) setLoading(true);
    else setLoadingMore(true);

    const params = new URLSearchParams();

    if (debouncedSearchTerm.startsWith("#")) {
      const tagsToSearch = debouncedSearchTerm
        .split(" ")
        .map((t) => t.trim().substring(1))
        .filter((t) => t.length > 0);

      if (tagsToSearch.length > 0) tagsToSearch.forEach((tag) => params.append("tag", tag));
    }

    if (appliedTagFilters.length > 0) {
      appliedTagFilters.forEach((tag) => params.append("tag", tag));
    }

    params.append("sortBy", sortBy);
    params.append("sortOrder", sortOrder);
    params.append("page", page.toString());
    params.append("limit", "21");
    params.append("locale", locale);

    if (showNSFW) params.append("nsfw", "true");

    const queryString = params.toString();

    fetch(`https://disparty.com/api/servers/search?${queryString}`, { credentials: "include" })
      .then((res) => res.json())
      .then((data) => {
        if (data && data.servers) {
          setServers((prev) => (page === 1 ? data.servers : [...prev, ...data.servers]));
          setHasMore(data.hasMore);
        } else {
          if (page === 1) setServers([]);
          setHasMore(false);
        }
      })
      .catch(() => {
        if (page === 1) setServers([]);
        setHasMore(false);
      })
      .finally(() => {
        setLoading(false);
        setLoadingMore(false);
      });
  }, [page, debouncedSearchTerm, sortBy, sortOrder, showNSFW, appliedTagFilters, locale]);

  useEffect(() => {
    const normalizedTerm = debouncedSearchTerm.trim();
    const isTextSearch = normalizedTerm.length > 0 && !normalizedTerm.startsWith("#");

    if (activeTab !== "servers" || !isTextSearch) return;
    if (loading || loadingMore || !hasMore) return;
    if (servers.length === 0 || filteredServers.length > 0) return;
    if (descriptionSearchPrefetchRef.current >= DESCRIPTION_SEARCH_PREFETCH_LIMIT) return;

    descriptionSearchPrefetchRef.current += 1;
    setPage((prevPage) => prevPage + 1);
  }, [activeTab, debouncedSearchTerm, loading, loadingMore, hasMore, servers.length, filteredServers.length]);

  useEffect(() => {
    fetch(`https://disparty.com/api/tags?locale=${locale}`)
      .then((res) => res.json())
      .then((data) => setTags(data || []))
      .catch(() => setTags([]));
  }, [locale]);

  useEffect(() => {
    const currentTarget = observerTarget.current;
    if (!currentTarget) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loading && !loadingMore && activeTab === "servers") {
          setPage((prevPage) => prevPage + 1);
        }
      },
      { rootMargin: "0px 0px 300px 0px", threshold: 0.01 }
    );

    observer.observe(currentTarget);

    return () => {
      observer.unobserve(currentTarget);
    };
  }, [observerTarget, hasMore, loading, loadingMore, activeTab]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") {
        setSelectedServer(null);
        setIsCommentModalOpen(false);
        setIsSearchPanelOpen(false);
        setIsTagSearch(false);
        setTagSuggestions([]);
      }
    };

    if (selectedServer || isCommentModalOpen || isSearchPanelOpen) window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [selectedServer, isCommentModalOpen, isSearchPanelOpen]);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);

    if (value.startsWith("#")) {
      setIsTagSearch(true);
      const parts = value.split(" ");
      const lastPart = parts[parts.length - 1];

      if (lastPart.startsWith("#")) {
        const tagQuery = lastPart.substring(1).toLowerCase();
        if (tagQuery) {
          const suggestions = tags.filter((tag) => tag.name.toLowerCase().includes(tagQuery)).slice(0, 5);
          setTagSuggestions(suggestions);
        } else {
          setTagSuggestions(tags.slice(0, 5));
        }
      } else {
        setTagSuggestions([]);
      }
    } else {
      setIsTagSearch(false);
      setTagSuggestions([]);
    }
  };

  const selectTag = (tagName) => {
    const parts = searchTerm.split(" ");
    parts.pop();
    parts.push(`#${tagName}`);
    setSearchTerm(parts.join(" ") + " ");
    setIsTagSearch(true);
    setTagSuggestions([]);
    if (searchInputRef.current) searchInputRef.current.focus();
  };

  const handleNSFWToggle = () => {
    if (showNSFW) setShowNSFW(false);
    else {
      if (window.confirm(tx("18금(NSFW) 콘텐츠를 검색 결과에 포함합니다.\n\n본인이 성인(18세 이상)이신가요?", "Bạn muốn hiển thị nội dung 18+ (NSFW)?\n\nBạn có trên 18 tuổi không?"))) {
        setShowNSFW(true);
      }
    }
  };

  const seoMeta = useMemo(() => {
    if (isVi) {
      return {
        title: "Server Discord Việt Nam | Quảng bá server Discord - Disparty",
        description: "Disparty giúp tìm và quảng bá server Discord Việt Nam với tag, sắp xếp và bộ lọc.",
        keywords:
          "server discord viet nam, quang ba server discord, tim server discord, danh sach server discord, sv discord, server dc, discord vietnam server, discord server vietnam, discord server list, 디코 서버",
      };
    }

    return {
      title: "Disparty 디스파티 - 디스코드 서버 홍보와 디코 서버 찾기",
      description: "Disparty(디스파티)는 한국 디스코드 서버를 찾고 홍보하는 플랫폼입니다. 디코 서버를 태그와 소개로 탐색하고 내 서버를 무료로 등록해 알릴 수 있습니다.",
      keywords:
        "디스파티, Disparty, 디스코드 서버, 디스코드 서버 홍보, 디코 서버, 디코 서버 홍보",
    };
  }, [isVi]);

  const canonicalUrl = `https://disparty.com/${locale}`;
  const alternateKoUrl = "https://disparty.com/ko";
  const alternateViUrl = "https://disparty.com/vi";

  return (
    <div className="min-h-screen bg-[#111] flex flex-col feed-fadeinup relative">
      <Helmet>
        <html lang={isVi ? "vi-VN" : "ko-KR"} />
        <title>{seoMeta.title}</title>
        <meta name="description" content={seoMeta.description} />
        <meta name="keywords" content={seoMeta.keywords} />
        <link rel="canonical" href={canonicalUrl} />
        <link rel="alternate" hrefLang="ko" href={alternateKoUrl} />
        <link rel="alternate" hrefLang="vi" href={alternateViUrl} />
        <link rel="alternate" hrefLang="x-default" href={alternateKoUrl} />
        <meta property="og:title" content={seoMeta.title} />
        <meta property="og:description" content={seoMeta.description} />
        <meta property="og:url" content={canonicalUrl} />
      </Helmet>

      <div className="flex w-full flex-1 justify-center gap-6 px-4 pt-6 pb-14 sm:px-6">
        {/* ✅ Kakao AdFit 왼쪽 광고 (기존 유닛) */}
        {!isElectron && (
          <div className="hidden 2xl:block w-[160px] sticky top-6 h-fit shrink-0">
            <AdFitBanner unit={ADFIT_LEFT_UNIT} />
          </div>
        )}

        <div className="flex-1 max-w-6xl flex flex-col items-center min-w-0 pb-16">
          <div className="mb-5 flex w-full flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <header className="min-w-0">
              <h1 className="font-maple text-2xl font-normal text-white sm:text-[1.75rem]">
                Disparty {!isVi && <span className="text-gray-400">디스파티</span>}
              </h1>
              <p className="mt-1 max-w-2xl text-sm leading-6 text-gray-500">
                {tx(
                  "디스코드·디코 서버를 태그와 소개로 찾고, 내 서버를 무료로 홍보하세요.",
                  "Tim cộng đồng Discord theo tag và giới thiệu server của bạn.",
                )}
              </p>
            </header>
            <CountryDropdown
              value={locale}
              options={countryOptions}
              onChange={handleLocaleChange}
              ariaLabel={tx("국가 선택", "Chọn quốc gia")}
            />
          </div>

          <div className="w-full flex justify-center mb-8 gap-3 sm:gap-4 flex-wrap">
            <motion.button
              onClick={() => changeActiveTab("servers")}
              whileTap={prefersReducedMotion ? undefined : { scale: 0.96 }}
              className={`relative isolate overflow-hidden border px-5 py-2.5 rounded-full font-bold text-sm transition-[color,border-color,box-shadow] duration-150 ease-out transform-gpu ${
                activeTab === "servers"
                  ? "border-transparent text-white shadow-md"
                  : "border-[#404249] bg-[#2b2c31] text-gray-400 hover:text-white"
              }`}
            >
              {activeTab === "servers" && (
                <motion.span
                  layoutId="feed-active-tab"
                  className="absolute inset-0 z-0 bg-[#5865F2]"
                  transition={prefersReducedMotion ? { duration: 0 } : { duration: 0.22, ease: [0.2, 0.8, 0.2, 1] }}
                />
              )}
              <span className="relative z-10">{tx("서버 찾기", "Tìm server")}</span>
            </motion.button>
            {!isVi && (
              <motion.button
                onClick={() => changeActiveTab("shop")}
                whileTap={prefersReducedMotion ? undefined : { scale: 0.96 }}
                className={`relative isolate overflow-hidden border px-5 py-2.5 rounded-full font-bold text-sm transition-[color,border-color,box-shadow] duration-150 ease-out transform-gpu ${
                  activeTab === "shop"
                    ? "border-transparent text-white shadow-md"
                    : "border-[#404249] bg-[#2b2c31] text-gray-400 hover:text-white"
                }`}
              >
                {activeTab === "shop" && (
                  <motion.span
                    layoutId="feed-active-tab"
                    className="absolute inset-0 z-0 bg-[#5865F2]"
                    transition={prefersReducedMotion ? { duration: 0 } : { duration: 0.22, ease: [0.2, 0.8, 0.2, 1] }}
                  />
                )}
                <span className="relative z-10">상점</span>
              </motion.button>
            )}
            <motion.button
              onClick={() => changeActiveTab("partybot")}
              whileTap={prefersReducedMotion ? undefined : { scale: 0.96 }}
              className={`relative isolate overflow-hidden border px-5 py-2.5 rounded-full font-bold text-sm transition-[color,border-color,box-shadow] duration-150 ease-out transform-gpu ${
                activeTab === "partybot"
                  ? "border-transparent text-white shadow-md"
                  : "border-[#404249] bg-[#2b2c31] text-gray-400 hover:text-white"
              }`}
            >
              {activeTab === "partybot" && (
                <motion.span
                  layoutId="feed-active-tab"
                  className="absolute inset-0 z-0 bg-[#5865F2]"
                  transition={prefersReducedMotion ? { duration: 0 } : { duration: 0.22, ease: [0.2, 0.8, 0.2, 1] }}
                />
              )}
              <span className="relative z-10">{tx("파티봇 관리", "Quản lý bot")}</span>
            </motion.button>
          </div>

          <AnimatePresence initial={false} mode="wait" custom={tabDirection}>
            <motion.div
              key={activeTab}
              custom={tabDirection}
              initial={prefersReducedMotion ? false : { opacity: 0, x: tabDirection * 18, y: 4 }}
              animate={{ opacity: 1, x: 0, y: 0 }}
              exit={prefersReducedMotion ? { opacity: 0 } : { opacity: 0, x: tabDirection * -12, y: -2 }}
              transition={{ duration: prefersReducedMotion ? 0 : 0.2, ease: [0.2, 0.8, 0.2, 1] }}
              className="min-h-[420px] w-full transform-gpu will-change-transform"
            >
          {activeTab === "servers" && (
            <>
              <div className="w-full mb-6">
                <div className="flex flex-col md:flex-row gap-4">
                  <button
                    type="button"
                    onClick={openSearchPanel}
                    className="flex-1 flex items-center gap-3 px-4 py-2.5 bg-[#1e1e1e] border border-[#333] rounded-xl hover:border-[#5865F2] transition-colors text-left"
                  >
                    <svg className="w-5 h-5 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M21 21l-4.35-4.35m1.1-4.4a7.5 7.5 0 11-15 0 7.5 7.5 0 0115 0z"
                      />
                    </svg>
                    <span className={`truncate text-sm ${hasActiveSearchFilters ? "text-gray-100" : "text-gray-400"}`}>{searchSummaryText}</span>
                  </button>

                  <SortDropdown
                    value={sortBy}
                    options={sortByOptions}
                    onChange={setSortBy}
                    ariaLabel={tx("정렬 기준 선택", "Chọn tiêu chí sắp xếp")}
                  />

                  <SortDropdown
                    value={sortOrder}
                    options={sortOrderOptions}
                    onChange={setSortOrder}
                    ariaLabel={tx("정렬 방향 선택", "Chọn hướng sắp xếp")}
                  />

                  <div className="flex items-center justify-center md:justify-start">
                    <label htmlFor="nsfw-toggle" className="flex items-center cursor-pointer select-none">
                      <span className={`text-sm font-medium mr-2 ${showNSFW ? "text-red-500" : "text-gray-400"}`}>
                        18+
                      </span>
                      <div className="relative">
                        <input
                          type="checkbox"
                          id="nsfw-toggle"
                          className="sr-only"
                          checked={showNSFW}
                          onChange={handleNSFWToggle}
                        />
                        <div className={`block w-12 h-6 rounded-full transition-colors ${showNSFW ? "bg-red-600" : "bg-gray-600"}`} />
                        <div className={`dot absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition-transform ${showNSFW ? "translate-x-6" : "translate-x-0"}`} />
                      </div>
                    </label>
                  </div>
                </div>
              </div>

              {createPortal(
                <AnimatePresence>
                  {isSearchPanelOpen && (
                    <>
                      <motion.button
                        type="button"
                        onClick={closeSearchPanel}
                        className="fixed inset-0 z-[12000] bg-black/75 will-change-opacity"
                        aria-label="close search panel"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: prefersReducedMotion ? 0 : 0.12, ease: "linear" }}
                      />

                      <div
                        className="fixed inset-0 z-[12001] overflow-y-auto px-4 pt-14 pb-8 md:pt-20"
                        onClick={closeSearchPanel}
                      >
                        <motion.div
                          className="w-full max-w-[640px] mx-auto rounded-2xl border border-[#5865F2] bg-[#1e1f26] shadow-[0_24px_80px_rgba(0,0,0,0.5)] p-5 transform-gpu will-change-transform"
                          initial={prefersReducedMotion ? false : { opacity: 0, y: -14 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={prefersReducedMotion ? { opacity: 0 } : { opacity: 0, y: -10 }}
                          transition={{
                            duration: prefersReducedMotion ? 0 : 0.16,
                            ease: [0.2, 0.8, 0.2, 1],
                          }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <div className="flex items-center gap-2">
                            <div className="relative flex-1">
                              <input
                                ref={searchInputRef}
                                type="text"
                                value={searchTerm}
                                onChange={handleSearchChange}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") {
                                    e.preventDefault();
                                    applySearchFilters();
                                  }
                                }}
                                placeholder={tx("찾고 싶은 서버를 입력하세요...", "Nhập server bạn muốn tìm...")}
                                className={`w-full pl-10 pr-3 py-2.5 bg-[#23252d] border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#5865F2] transition-colors ${
                                  isTagSearch ? "border-[#5865F2] text-blue-300" : "border-[#333] text-white"
                                }`}
                              />
                              <svg className="w-4 h-4 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M21 21l-4.35-4.35m1.1-4.4a7.5 7.5 0 11-15 0 7.5 7.5 0 0115 0z"
                                />
                              </svg>
                            </div>
                            <button
                              type="button"
                              onClick={applySearchFilters}
                              className="px-5 py-2.5 rounded-lg bg-[#5865F2] text-white font-semibold hover:bg-[#4a56d1] transition-colors"
                            >
                              {tx("검색", "Tìm")}
                            </button>
                          </div>

                          {isTagSearch && tagSuggestions.length > 0 && (
                            <div className="mt-2 bg-[#23252d] border border-[#333] rounded-lg shadow-lg max-h-52 overflow-y-auto">
                              {tagSuggestions.map((tag) => (
                                <button
                                  key={tag.name}
                                  onClick={() => selectTag(tag.name)}
                                  className="w-full text-left px-4 py-2 text-sm text-gray-300 hover:bg-[#2d303a] transition-colors"
                                >
                                  #{tag.name}
                                  <span className="text-xs text-gray-500 ml-2">({tag.count})</span>
                                </button>
                              ))}
                            </div>
                          )}

                          <div className="mt-5">
                            <div className="flex items-center justify-between mb-2">
                              <p className="text-xs font-semibold text-gray-500 tracking-[0.08em] uppercase">{tx("인기 태그", "Thẻ phổ biến")}</p>
                              <button
                                type="button"
                                onClick={resetSearchDraft}
                                className="text-xs text-gray-400 hover:text-white transition-colors"
                              >
                                {tx("초기화", "Đặt lại")}
                              </button>
                            </div>
                            <div className="flex flex-wrap gap-2">
                              {popularTags.map((tag) => {
                                const isSelected = draftTagFilters.includes(tag.name);
                                return (
                                  <button
                                    key={tag.name}
                                    type="button"
                                    onClick={() => toggleDraftTagFilter(tag.name)}
                                    className={`px-3 py-1 rounded-full border text-xs font-medium transition-colors ${
                                      isSelected
                                        ? "bg-[#5865F2]/20 border-[#5865F2] text-[#c9ceff]"
                                        : "bg-[#23252d] border-[#333] text-gray-300 hover:border-[#5865F2] hover:text-white"
                                    }`}
                                  >
                                    #{tag.name}
                                    <span className="ml-1 text-[11px] opacity-70">{tag.count}</span>
                                  </button>
                                );
                              })}
                            </div>
                          </div>

                        </motion.div>
                      </div>
                    </>
                  )}
                </AnimatePresence>,
                document.body
              )}

              {showServerListLoader ? (
                <div className="text-center text-gray-400 mt-10">{tx("서버 목록을 불러오는 중...", "Đang tải danh sách server...")}</div>
              ) : filteredServers.length === 0 ? (
                <div className="text-center text-gray-500 mt-10">{hasActiveSearchFilters ? tx("검색 결과가 없습니다.", "Không có kết quả.") : tx("등록된 서버가 없습니다.", "Chưa có server nào.")}</div>
              ) : (
                <>
                  <div className="relative w-full">
                    <AnimatePresence initial={false}>
                      {isServerListRefreshing && (
                        <motion.div
                          className="absolute -top-3 left-0 right-0 z-10 h-0.5 overflow-hidden rounded-full bg-[#5865F2]/20"
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                          transition={{ duration: prefersReducedMotion ? 0 : 0.12 }}
                          aria-hidden="true"
                        >
                          <motion.span
                            className="block h-full w-1/3 rounded-full bg-[#7c86ff]"
                            initial={prefersReducedMotion ? false : { x: "-100%" }}
                            animate={prefersReducedMotion ? undefined : { x: "300%" }}
                            transition={{ duration: 0.8, ease: "easeInOut", repeat: Infinity }}
                          />
                        </motion.div>
                      )}
                    </AnimatePresence>

                    <motion.div
                      layout
                      className="grid w-full grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3"
                      animate={{ opacity: isServerListRefreshing ? 0.86 : 1 }}
                      transition={{ duration: prefersReducedMotion ? 0 : 0.14, ease: "easeOut" }}
                    >
                    <AnimatePresence initial={false} mode="popLayout">
                      {decoratedServers.map((server) => {
                        return (
                          <motion.div
                            key={server.server_id}
                            layout="position"
                            initial={prefersReducedMotion ? false : { opacity: 0, y: 8 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={prefersReducedMotion ? { opacity: 0 } : { opacity: 0, scale: 0.98 }}
                            transition={{
                              duration: prefersReducedMotion ? 0 : 0.14,
                              ease: "easeOut",
                              layout: prefersReducedMotion
                                ? { duration: 0 }
                                : { duration: 0.32, ease: [0.2, 0.8, 0.2, 1] },
                            }}
                            className="transform-gpu will-change-transform"
                          >
                            <ServerCard
                              server={server}
                              onOpenDetails={openServerDetails}
                              onOpenComments={openCommentModal}
                              onTagClick={handleTagClick}
                              locale={locale}
                            />
                          </motion.div>
                        );
                      })}
                    </AnimatePresence>
                    </motion.div>
                  </div>

                  <div ref={observerTarget} className="h-10 w-full text-center py-4 mt-4">
                    {loadingMore && <div className="text-gray-400">{tx("서버를 더 불러오는 중...", "Đang tải thêm server...")}</div>}
                    {!loadingMore && !hasMore && <div className="text-gray-500">{tx("모든 서버를 불러왔습니다.", "Đã tải tất cả server.")}</div>}
                  </div>
                </>
              )}
            </>
          )}

          {activeTab === "partybot" && (
            <div className="mx-auto w-full max-w-7xl">
              {loadingAuth && !localPreview ? (
                <div className="text-center py-20 text-gray-400">
                  <div className="inline-block w-8 h-8 border-4 border-[#5865F2] border-t-transparent rounded-full animate-spin mb-4" />
                  <p>{tx("로그인 정보를 확인 중입니다...", "Đang kiểm tra đăng nhập...")}</p>
                </div>
              ) : !isLoggedIn && !localPreview ? (
                <div className="flex flex-col items-center justify-center bg-[#1e1e1e] border border-[#333] rounded-2xl p-10 shadow-2xl text-center my-10">
                  <div className="w-20 h-20 bg-[#5865F2]/10 rounded-full flex items-center justify-center mb-6">
                    <svg className="w-10 h-10 text-[#5865F2]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                      />
                    </svg>
                  </div>
                  <h2 className="text-2xl font-bold text-white mb-2">{tx("로그인이 필요합니다", "Cần đăng nhập")}</h2>
                  <p className="text-gray-400 mb-8 max-w-md">
                    {tx("파티봇 설정은 로그인이 필요합니다.", "Cần đăng nhập để cài đặt bot.")}
                    <br />
                    <strong>{tx("[서버 추가/프로필]", "[Thêm server/Hồ sơ]")}</strong> {tx("페이지에서 로그인 후 다시 와주세요.", "hãy đăng nhập rồi quay lại.")}
                  </p>

                  <div className="flex gap-3">
                    <button
                      onClick={handleLoginRedirect}
                      className="bg-[#5865F2] hover:bg-[#4752c4] text-white font-bold py-3 px-6 rounded-xl transition-colors shadow-lg flex items-center gap-2"
                    >
                      {tx("로그인하러 가기", "Đi đăng nhập")}
                    </button>
                  </div>
                </div>
              ) : (
                <PartyBotDashboard locale={locale} />
              )}
            </div>
          )}

          {!isVi && activeTab === "shop" && <ShopTest embedded />}
            </motion.div>
          </AnimatePresence>

        </div>

        {/* ✅ Kakao AdFit 오른쪽 광고 (신규 유닛) */}
        {!isElectron && (
          <div className="hidden 2xl:block w-[160px] sticky top-6 h-fit shrink-0">
            <AdFitBanner unit={ADFIT_RIGHT_UNIT} />
          </div>
        )}
      </div>

      <BusinessFooter locale={locale} />

      <DideokAssistant
        isLoggedIn={isLoggedIn}
        loadingAuth={loadingAuth}
        locale={locale}
        hidden={Boolean(selectedServer || isCommentModalOpen || isSearchPanelOpen)}
      />

      {/* 기존 모달들 유지 */}
      {createPortal(
        <AnimatePresence>
          {selectedServer && (
            <motion.div
              className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/70"
              onClick={() => setSelectedServer(null)}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: prefersReducedMotion ? 0 : 0.14, ease: "linear" }}
            >
              <motion.div
                className="w-full max-w-4xl max-h-[90vh] transform-gpu"
                onClick={(e) => e.stopPropagation()}
                initial={prefersReducedMotion ? false : { opacity: 0, y: 14, scale: 0.99 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={prefersReducedMotion ? { opacity: 0 } : { opacity: 0, y: 10, scale: 0.99 }}
                transition={{ duration: prefersReducedMotion ? 0 : 0.16, ease: [0.2, 0.8, 0.2, 1] }}
              >
                <ShopProductFrame
                  product={selectedServer?.shopProduct || shopProductById[selectedServer?.shop_background_product_id]}
                  className={`relative rounded-2xl w-full max-h-[90vh] shadow-2xl box-border font-${selectedServer.font || "default"}`}
                  style={{ backgroundColor: "#323339" }}
                  artworkMode="detail"
                  contentStyle={{ maxHeight: "min(90vh, calc(100dvh - 2rem))", overflow: "hidden" }}
                >
              <button onClick={() => setSelectedServer(null)} className="absolute right-4 top-4 z-40 text-gray-300 hover:text-white bg-black/20 rounded-full p-1 transition-colors">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>

              <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
              <div
                className="aspect-[23/10] w-full flex-none bg-center bg-cover rounded-t-2xl relative"
                style={{
                  backgroundImage: selectedServer.banner_image
                    ? `url(https://disparty.com/uploads/${selectedServer.banner_image}?t=${new Date(selectedServer.reloaded_at).getTime()})`
                    : "none",
                  backgroundColor: selectedServer.banner_image ? "transparent" : "#2a2a2a",
                }}
              />

              <div className="p-6">
                <div className="flex flex-col md:flex-row gap-6">
                  <div className="flex-shrink-0 -mt-16 md:-mt-20 relative z-10 mx-auto md:mx-0">
                    {getGuildIcon(
                      selectedServer,
                      "w-24 h-24 md:w-32 md:h-32 rounded-3xl border-[6px] border-[#323339] object-cover bg-[#323339] shadow-lg"
                    )}
                  </div>

                  <div className="flex-1 pt-1 min-w-0">
                    <h2 className="text-2xl font-bold text-white break-words flex items-center flex-wrap">
                      <span className="mr-2">{selectedServer.server_name}</span>
                      {selectedServer.is_nsfw && (
                        <span className="flex-shrink-0 px-2.5 py-0.5 bg-red-500/20 border border-red-500 text-red-400 text-xs font-semibold rounded-full">
                          18+
                        </span>
                      )}
                    </h2>

                    <div className="flex flex-wrap gap-2 mt-2 mb-3">
                      {(selectedServer.tags || "")
                        .split(",")
                        .filter((t) => t.trim())
                        .map((tag, i) => (
                          <button
                            key={i}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleTagClick(tag.trim());
                            }}
                            className="bg-[#121212] border border-[#333] text-[#b5b5b5] text-xs font-medium px-3 py-1 rounded-full whitespace-nowrap hover:border-[#5865F2] hover:text-[#5865F2] transition-colors"
                          >
                            #{tag.trim()}
                          </button>
                        ))}
                    </div>

                    <div className="flex items-center gap-4 mt-2 text-xs text-gray-400 mb-4">
                      {selectedServer.member_count !== undefined && (
                        <div className="flex items-center">
                          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                            />
                          </svg>
                          {selectedServer.member_count.toLocaleString()}
                          {tx("명", " người")}
                        </div>
                      )}
                      <p>{selectedServer.reloaded_at ? timeAgo(selectedServer.reloaded_at, locale) : ""}</p>
                    </div>
                  </div>
                </div>

                <div className="mt-6 text-gray-300 prose prose-invert max-w-none break-words bg-[#2b2c31] p-4 rounded-lg border border-[#3f4148]">
                  <ReactMarkdown
                    remarkPlugins={[remarkBreaks]}
                    components={{
                      p: ({ node, ...props }) => <p className="my-2 leading-relaxed" {...props} />,
                      a: ({ node, ...props }) => <a className="text-[#5865F2] hover:underline break-all" {...props} />,
                      ul: ({ node, ...props }) => <ul className="list-disc pl-5 my-2" {...props} />,
                      ol: ({ node, ...props }) => <ol className="list-decimal pl-5 my-2" {...props} />,
                      li: ({ node, ...props }) => <li className="my-1 leading-snug" {...props} />,
                      img: ({ node, ...props }) => <img className="max-w-full h-auto rounded-md" {...props} />,
                    }}
                  >
                    {selectedServer.description || tx("서버 설명이 없습니다.", "Không có mô tả server.")}
                  </ReactMarkdown>
                </div>
              </div>
              </div>

                <div className="relative z-40 flex flex-none justify-end border-t border-gray-700/50 bg-[#323339] px-5 py-4 sm:px-6">
                  {selectedServer.invite_url ? (
                    <a
                      href={selectedServer.invite_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="relative z-40 w-full sm:w-auto text-center px-6 py-3 rounded-xl bg-[#5865F2] text-white font-bold hover:bg-[#4a56d1] transition-colors shadow-lg flex items-center justify-center gap-2"
                    >
                      {tx("서버 참가하기", "Tham gia server")}
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                      </svg>
                    </a>
                  ) : (
                    <button className="relative z-40 w-full sm:w-auto px-6 py-3 rounded-xl bg-gray-600 text-white opacity-70 cursor-not-allowed">{tx("서버링크없음", "Không có link mời")}</button>
                  )}
                </div>
                </ShopProductFrame>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}

      {createPortal(
        <AnimatePresence>
          {isCommentModalOpen && selectedServerForComments && (
            <motion.div
              className="fixed inset-0 z-[10000] flex items-center justify-center p-4 bg-black/70"
              onClick={() => setIsCommentModalOpen(false)}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: prefersReducedMotion ? 0 : 0.14, ease: "linear" }}
            >
              <motion.div
                className="relative bg-[#323339] rounded-2xl w-full max-w-lg shadow-2xl flex flex-col max-h-[80vh] transform-gpu"
                onClick={(e) => e.stopPropagation()}
                initial={prefersReducedMotion ? false : { opacity: 0, y: 14, scale: 0.99 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={prefersReducedMotion ? { opacity: 0 } : { opacity: 0, y: 10, scale: 0.99 }}
                transition={{ duration: prefersReducedMotion ? 0 : 0.16, ease: [0.2, 0.8, 0.2, 1] }}
              >
              <div className="p-4 border-b border-gray-700/50 flex flex-col gap-3">
                <div className="flex items-center justify-between">
                  <h3 className="text-white font-bold text-lg flex items-center gap-2">
                    💬 {tx("익명 댓글", "Bình luận ẩn danh")}
                    <span className="text-xs bg-[#5865F2] px-2 py-0.5 rounded-full text-white">{comments.length}</span>
                  </h3>
                  <button onClick={() => setIsCommentModalOpen(false)} className="text-gray-400 hover:text-white">
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      setCommentSort("newest");
                      fetchComments(selectedServerForComments.server_id, "newest");
                    }}
                    className={`text-xs px-3 py-1.5 rounded-full transition-colors font-medium ${
                      commentSort === "newest"
                        ? "bg-white text-black"
                        : "bg-[#1e1e1e] text-gray-400 hover:text-white border border-[#404249]"
                    }`}
                  >
                    {tx("최신순", "Mới nhất")}
                  </button>
                  <button
                    onClick={() => {
                      setCommentSort("likes");
                      fetchComments(selectedServerForComments.server_id, "likes");
                    }}
                    className={`text-xs px-3 py-1.5 rounded-full transition-colors font-medium ${
                      commentSort === "likes"
                        ? "bg-white text-black"
                        : "bg-[#1e1e1e] text-gray-400 hover:text-white border border-[#404249]"
                    }`}
                  >
                    🔥 {tx("인기순", "Phổ biến")}
                  </button>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto p-4 min-h-[300px]">
                {loadingComments ? (
                  <div className="text-center text-gray-500 py-10">{tx("댓글을 불러오는 중...", "Đang tải bình luận...")}</div>
                ) : comments.length === 0 ? (
                  <div className="text-center text-gray-500 py-10 flex flex-col items-center">
                    <span className="text-4xl mb-2">📭</span>
                    {tx("아직 작성된 댓글이 없습니다.", "Chưa có bình luận nào.")}
                    <br />
                    {tx("첫 댓글을 남겨보세요!", "Hãy để lại bình luận đầu tiên!")}
                  </div>
                ) : (
                  comments.map((comment) => (
                    <CommentItem key={comment.id} comment={comment} onLike={handleLike} onReply={handleReply} onDeleteReply={handleDeleteReply} locale={locale} />
                  ))
                )}
              </div>

              <div className="p-4 bg-[#2b2c31] rounded-b-2xl border-t border-gray-700/50">
                <div className="relative">
                  <textarea
                    className="w-full bg-[#1e1e1e] text-white text-sm p-3 rounded-lg border border-[#404249] focus:outline-none focus:border-[#5865F2] resize-none"
                    rows="3"
                    placeholder={tx("익명으로 댓글을 남겨보세요 (최대 500자)", "Để lại bình luận ẩn danh (tối đa 500 ký tự)")}
                    maxLength={500}
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        handlePostComment();
                      }
                    }}
                  />
                  <div className="flex justify-between items-center mt-2">
                    <span className="text-xs text-gray-500">{newComment.length} / 500</span>
                    <button
                      onClick={handlePostComment}
                      disabled={!newComment.trim()}
                      className={`px-4 py-1.5 rounded-md text-sm font-bold transition-colors ${
                        newComment.trim()
                          ? "bg-[#5865F2] text-white hover:bg-[#4a56d1]"
                          : "bg-gray-600 text-gray-400 cursor-not-allowed"
                      }`}
                    >
                      {tx("등록", "Đăng")}
                    </button>
                  </div>
                </div>
              </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}
    </div>
  );
}

function PartyBotDashboard({ locale = "ko" }) {
  const isVi = locale === "vi";
  const tx = useCallback((ko, vi) => (isVi ? vi : ko), [isVi]);
  const prefersReducedMotion = useReducedMotion();
  const navigate = useNavigate();
  const botInviteLink =
    "https://discord.com/oauth2/authorize?client_id=1433494628711137290&permissions=8&integration_type=0&scope=bot";
  const localPreview = window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1";

  const [servers, setServers] = useState([]);
  const [selectedServerId, setSelectedServerId] = useState("");
  const [loadingServers, setLoadingServers] = useState(true);
  const [loadingChannels, setLoadingChannels] = useState(false);
  const [connectionError, setConnectionError] = useState("");
  const [activeFeature, setActiveFeature] = useState("reaction");
  const [featureDirection, setFeatureDirection] = useState(1);
  const [savingFeature, setSavingFeature] = useState("");
  const [notice, setNotice] = useState(null);
  const [channels, setChannels] = useState({ categories: [], text: [], voice: [], roles: [] });
  const [reactionForm, setReactionForm] = useState({
    channelId: "",
    items: [{ roleId: "", emoji: "✅" }],
    message: tx("아래 이모지를 눌러 역할을 받아가세요!", "Nhấn emoji bên dưới để nhận vai trò!"),
  });
  const [voiceForm, setVoiceForm] = useState({
    generatorId: "",
    categoryId: "",
    namePattern: tx("{user}님의 파티", "Phòng của {user}"),
  });
  const [voiceConfigured, setVoiceConfigured] = useState(false);
  const [reactionConfigs, setReactionConfigs] = useState([]);
  const [welcomeConfig, setWelcomeConfig] = useState(null);
  const [foodOwned, setFoodOwned] = useState(false);

  const selectedServer = useMemo(
    () => servers.find((server) => server.server_id === selectedServerId),
    [servers, selectedServerId]
  );
  const serverOptions = useMemo(
    () => servers.map((server) => ({ value: server.server_id, label: server.server_name })),
    [servers]
  );
  const reactionChannelOptions = useMemo(
    () => channels.text.map((channel) => ({ value: channel.id, label: `# ${channel.name}` })),
    [channels.text]
  );
  const roleOptions = useMemo(
    () =>
      [...channels.roles]
        .sort((a, b) => b.position - a.position)
        .map((role) => ({ value: role.id, label: `@${role.name}` })),
    [channels.roles]
  );
  const voiceChannelOptions = useMemo(
    () => channels.voice.map((channel) => ({ value: channel.id, label: channel.name })),
    [channels.voice]
  );
  const categoryOptions = useMemo(
    () => [
      { value: "", label: tx("현재 채널과 같은 위치", "Cùng vị trí với kênh hiện tại") },
      ...channels.categories.map((channel) => ({ value: channel.id, label: channel.name })),
    ],
    [channels.categories, tx]
  );

  useEffect(() => {
    let cancelled = false;

    const loadServers = async () => {
      setLoadingServers(true);
      if (localPreview) {
        setServers([{ server_id: "preview-server", server_name: "디덕이 놀이터", icon: "" }]);
        setSelectedServerId("preview-server");
        setLoadingServers(false);
        return;
      }
      try {
        const response = await fetch("https://disparty.com/api/servers/mine", { credentials: "include" });
        const data = await response.json().catch(() => null);
        if (!response.ok) {
          throw new Error(data?.message || tx("서버 목록을 불러오지 못했습니다.", "Không thể tải danh sách server."));
        }
        if (cancelled) return;

        const nextServers = Array.isArray(data) ? data : [];
        setServers(nextServers);
        setSelectedServerId((current) => {
          if (nextServers.some((server) => server.server_id === current)) return current;
          return nextServers[0]?.server_id || "";
        });
      } catch (error) {
        if (!cancelled) {
          setServers([]);
          setConnectionError(error.message);
        }
      } finally {
        if (!cancelled) setLoadingServers(false);
      }
    };

    loadServers();
    return () => {
      cancelled = true;
    };
  }, [localPreview, tx]);

  useEffect(() => {
    if (!selectedServerId) {
      setChannels({ categories: [], text: [], voice: [], roles: [] });
      return undefined;
    }

    let cancelled = false;
    const loadBotSettings = async () => {
      setLoadingChannels(true);
      setConnectionError("");
      setNotice(null);

      if (localPreview) {
        setChannels({
          categories: [{ id: "category-1", name: "커뮤니티" }],
          text: [{ id: "general", name: "일반" }, { id: "welcome", name: "환영" }, { id: "goodbye", name: "퇴장-알림" }],
          voice: [{ id: "voice-1", name: "➕ 음성방 만들기" }],
          roles: [{ id: "role-1", name: "게임 알림", position: 3 }, { id: "role-2", name: "공지 알림", position: 2 }],
        });
        setVoiceConfigured(false);
        setReactionConfigs([]);
        setWelcomeConfig(null);
        setFoodOwned(false);
        setReactionForm((current) => ({ ...current, channelId: "general", items: [{ roleId: "role-1", emoji: "✅" }] }));
        setVoiceForm({ generatorId: "voice-1", categoryId: "category-1", namePattern: "{user}님의 파티" });
        setLoadingChannels(false);
        return;
      }

      try {
        const [channelsResponse, settingsResponse] = await Promise.all([
          fetch(`https://disparty.com/api/servers/${selectedServerId}/channels`, { credentials: "include" }),
          fetch(`https://disparty.com/api/servers/${selectedServerId}/bot-settings`, { credentials: "include", cache: "no-store" }),
        ]);
        const [channelData, settingsData] = await Promise.all([
          channelsResponse.json().catch(() => ({})),
          settingsResponse.json().catch(() => ({})),
        ]);

        if (!channelsResponse.ok) {
          throw new Error(channelData?.message || tx("Discord 정보를 불러오지 못했습니다.", "Không thể tải dữ liệu Discord."));
        }
        if (!settingsResponse.ok) {
          throw new Error(settingsData?.message || tx("저장된 설정을 불러오지 못했습니다.", "Không thể tải cài đặt đã lưu."));
        }
        if (cancelled) return;

        const nextChannels = {
          categories: channelData.categories || [],
          text: channelData.text_channels || [],
          voice: channelData.voice_channels || [],
          roles: channelData.roles || [],
        };
        const sortedRoles = [...nextChannels.roles].sort((a, b) => b.position - a.position);
        const savedVoice = settingsData.voice_config;

        setChannels(nextChannels);
        setVoiceConfigured(Boolean(savedVoice));
        setReactionConfigs(settingsData.reaction_roles || []);
        setWelcomeConfig(settingsData.welcome_config || null);
        setFoodOwned(Boolean(settingsData.dideok_food));
        setReactionForm({
          channelId: nextChannels.text[0]?.id || "",
          items: [{ roleId: sortedRoles[0]?.id || "", emoji: "✅" }],
          message: tx("아래 이모지를 눌러 역할을 받아가세요!", "Nhấn emoji bên dưới để nhận vai trò!"),
        });
        setVoiceForm({
          generatorId:
            nextChannels.voice.some((channel) => channel.id === savedVoice?.generatorId)
              ? savedVoice.generatorId
              : nextChannels.voice[0]?.id || "",
          categoryId:
            nextChannels.categories.some((channel) => channel.id === savedVoice?.categoryId)
              ? savedVoice.categoryId
              : "",
          namePattern: savedVoice?.namePattern || tx("{user}님의 파티", "Phòng của {user}"),
        });
      } catch (error) {
        if (!cancelled) {
          setChannels({ categories: [], text: [], voice: [], roles: [] });
          setVoiceConfigured(false);
          setReactionConfigs([]);
          setConnectionError(error.message);
        }
      } finally {
        if (!cancelled) setLoadingChannels(false);
      }
    };

    loadBotSettings();
    return () => {
      cancelled = true;
    };
  }, [localPreview, selectedServerId, tx]);

  const handleSave = async (type) => {
    if (!selectedServerId || savingFeature) return;

    if (type === "reaction" && (!reactionForm.channelId || !reactionForm.message.trim() || reactionForm.items.some((item) => !item.roleId || !item.emoji.trim()))) {
      setNotice({ type: "error", message: tx("채널, 역할, 이모지, 메시지를 모두 선택해 주세요.", "Hãy chọn đầy đủ kênh, vai trò, emoji và tin nhắn.") });
      return;
    }
    if (type === "reaction" && new Set(reactionForm.items.map((item) => item.emoji.trim())).size !== reactionForm.items.length) {
      setNotice({ type: "error", message: tx("같은 이모지는 중복 사용할 수 없습니다.", "Không thể dùng trùng emoji.") });
      return;
    }
    if (type === "reaction" && new Set(reactionForm.items.map((item) => item.roleId)).size !== reactionForm.items.length) {
      setNotice({ type: "error", message: tx("같은 역할은 중복 설정할 수 없습니다.", "Không thể cài trùng vai trò.") });
      return;
    }
    if (type === "voice" && (!voiceForm.generatorId || !voiceForm.namePattern.trim())) {
      setNotice({ type: "error", message: tx("입장 채널과 생성될 채널 이름을 입력해 주세요.", "Hãy chọn kênh vào và nhập tên kênh sẽ tạo.") });
      return;
    }
    if (type === "voice" && !voiceForm.namePattern.includes("{user}")) {
      setNotice({ type: "error", message: tx("채널 이름에 {user}를 포함해 주세요.", "Tên kênh phải có {user}.") });
      return;
    }

    setSavingFeature(type);
    setNotice(null);
    if (localPreview) {
      setNotice({ type: "success", message: "로컬 미리보기에서 설정 화면을 확인했습니다." });
      setSavingFeature("");
      if (type === "voice") setVoiceConfigured(true);
      return;
    }
    const endpoint = type === "reaction" ? "/api/bot/reaction-role" : "/api/bot/voice-config";
    const payload = type === "reaction"
      ? {
          guildId: selectedServerId,
          channelId: reactionForm.channelId,
          message: reactionForm.message.trim(),
          items: reactionForm.items.map((item) => ({ roleId: item.roleId, emoji: item.emoji.trim() })),
        }
      : { guildId: selectedServerId, ...voiceForm, namePattern: voiceForm.namePattern.trim() };

    try {
      const response = await fetch(`https://disparty.com${endpoint}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
        credentials: "include",
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data?.message || tx("설정을 저장하지 못했습니다.", "Không thể lưu cài đặt."));
      }
      setNotice({
        type: "success",
        message: type === "reaction"
          ? tx("Discord에 역할 버튼 메시지를 보냈습니다.", "Đã gửi tin nhắn vai trò đến Discord.")
          : tx("자동 음성방 설정을 저장했습니다.", "Đã lưu cài đặt phòng thoại tự động."),
      });
      if (type === "voice") setVoiceConfigured(true);
      if (type === "reaction" && data.messageId) {
        setReactionConfigs((current) => [...current, { messageId: data.messageId, channelId: data.channelId, itemCount: data.itemCount }]);
      }
    } catch (error) {
      setNotice({ type: "error", message: error.message });
    } finally {
      setSavingFeature("");
    }
  };

  const handleDisableVoice = async () => {
    if (!selectedServerId || savingFeature) return;
    setSavingFeature("voice-disable");
    setNotice(null);
    try {
      const response = await fetch(
        `https://disparty.com/api/bot/voice-config?guildId=${encodeURIComponent(selectedServerId)}`,
        { method: "DELETE", credentials: "include" }
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data?.message || tx("자동 음성방을 끄지 못했습니다.", "Không thể tắt phòng thoại tự động."));
      }
      setVoiceConfigured(false);
      setNotice({ type: "success", message: tx("자동 음성방을 껐습니다.", "Đã tắt phòng thoại tự động.") });
    } catch (error) {
      setNotice({ type: "error", message: error.message });
    } finally {
      setSavingFeature("");
    }
  };

  const handleDeleteReaction = async (messageId) => {
    if (!selectedServerId || savingFeature) return;
    if (!window.confirm(tx("이 역할 버튼 메시지를 삭제할까요?", "Xóa tin nhắn nút vai trò này?"))) return;
    setSavingFeature(`reaction-delete-${messageId}`);
    setNotice(null);
    try {
      const response = await fetch(`https://disparty.com/api/bot/reaction-role?guildId=${encodeURIComponent(selectedServerId)}&messageId=${encodeURIComponent(messageId)}`, { method: "DELETE", credentials: "include" });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data?.message || tx("역할 버튼을 삭제하지 못했습니다.", "Không thể xóa nút vai trò."));
      setReactionConfigs((current) => current.filter((item) => item.messageId !== messageId));
      setNotice({ type: "success", message: data.message });
    } catch (error) {
      setNotice({ type: "error", message: error.message });
    } finally {
      setSavingFeature("");
    }
  };

  const textInputClass =
    "w-full rounded-lg border border-[#404249] bg-[#232428] px-4 py-3 text-sm text-white outline-none transition-colors placeholder:text-gray-500 hover:border-[#555861] focus:border-[#5865F2] focus:ring-2 focus:ring-[#5865F2]/25";
  const changeFeature = (nextFeature) => {
    if (nextFeature === activeFeature) return;
    const order = ["reaction", "voice", "welcome"];
    setFeatureDirection(order.indexOf(nextFeature) > order.indexOf(activeFeature) ? 1 : -1);
    setActiveFeature(nextFeature);
    setNotice(null);
  };
  const motionProps = prefersReducedMotion
    ? { initial: false, animate: { opacity: 1 }, exit: { opacity: 0 }, transition: { duration: 0 } }
    : { initial: { opacity: 0, x: featureDirection * 42 }, animate: { opacity: 1, x: 0 }, exit: { opacity: 0, x: featureDirection * -28 }, transition: { duration: 0.3, ease: [0.22, 1, 0.36, 1] } };
  const indicatorTransition = prefersReducedMotion ? { duration: 0 } : { type: "spring", stiffness: 420, damping: 34 };

  if (loadingServers) {
    return (
      <div className="min-h-[420px] border border-[#33353b] bg-[#1b1c1f] flex items-center justify-center rounded-lg">
        <LoaderCircle className="h-6 w-6 animate-spin text-[#7c86ff]" aria-hidden="true" />
        <span className="ml-3 text-sm text-gray-400">{tx("서버를 불러오는 중", "Đang tải server")}</span>
      </div>
    );
  }

  if (servers.length === 0) {
    return (
      <div className="min-h-[420px] border border-[#33353b] bg-[#1b1c1f] px-6 py-14 text-center rounded-lg">
        <img
          src="/dideok-crying.png?v=20260805"
          alt={tx("눈물을 흘리는 디덕이", "Dideok đang khóc")}
          className="mx-auto h-32 w-32 rounded-lg object-cover shadow-[0_16px_36px_rgba(0,0,0,0.3)] sm:h-40 sm:w-40"
        />
        <h3 className="mt-5 text-lg font-bold text-white">{tx("관리할 서버가 없습니다", "Không có server để quản lý")}</h3>
        <p className="mx-auto mt-2 max-w-md text-sm leading-6 text-gray-400">
          {connectionError || tx("먼저 디스파티에 서버를 추가해 주세요.", "Hãy thêm server vào Disparty trước.")}
        </p>
        <button
          type="button"
          onClick={() => navigate(withLocale("/profile", locale))}
          className="mt-6 rounded-lg bg-[#5865F2] px-5 py-2.5 text-sm font-bold text-white transition-colors hover:bg-[#4752c4]"
        >
          {tx("서버 추가", "Thêm server")}
        </button>
      </div>
    );
  }

  return (
    <section className="overflow-visible rounded-lg border border-[#33353b] bg-[#1b1c1f]">
      <header className="flex flex-col gap-5 px-5 py-5 sm:flex-row sm:items-center sm:justify-between sm:px-6">
        <div className="flex items-center gap-3">
          <img
            src="/dideok-assistant.png?v=20260805"
            alt=""
            className="h-10 w-10 flex-none object-contain drop-shadow-[0_5px_10px_rgba(0,0,0,0.32)]"
          />
          <div>
            <h2 className="text-lg font-bold text-white">{tx("파티봇 관리", "Quản lý PartyBot")}</h2>
            <p className="mt-0.5 text-xs text-gray-500">{tx("서버 자동화 설정", "Cài đặt tự động hóa server")}</p>
          </div>
        </div>

        <div className="w-full sm:max-w-[320px]">
          <label className="mb-1.5 block text-xs font-semibold text-gray-400">{tx("관리할 서버", "Server cần quản lý")}</label>
          <FormDropdown
            value={selectedServerId}
            options={serverOptions}
            onChange={setSelectedServerId}
            placeholder={tx("서버 선택", "Chọn server")}
            ariaLabel={tx("관리할 서버 선택", "Chọn server để quản lý")}
          />
        </div>
      </header>

      {selectedServer && (
        <div className="flex items-center gap-3 border-t border-[#303238] bg-[#18191c] px-5 py-3 sm:px-6">
          <div className="flex min-w-0 items-center gap-2.5">
            {selectedServer.icon ? (
              <img
                src={`https://cdn.discordapp.com/icons/${selectedServer.server_id}/${selectedServer.icon}.png?size=64`}
                alt=""
                className="h-7 w-7 flex-none rounded-full object-cover"
              />
            ) : (
              <div className="flex h-7 w-7 flex-none items-center justify-center rounded-full bg-[#35373d] text-xs font-bold text-white">
                {selectedServer.server_name?.[0] || "D"}
              </div>
            )}
            <span className="truncate text-sm font-semibold text-gray-200">{selectedServer.server_name}</span>
          </div>
        </div>
      )}

      <div className="grid min-h-[510px] lg:grid-cols-[220px_minmax(0,1fr)]">
        <nav className="grid grid-cols-3 border-b border-[#303238] bg-[#191a1d] lg:block lg:border-b-0 lg:border-r" aria-label={tx("파티봇 기능", "Tính năng PartyBot")}>
          <button
            type="button"
            onClick={() => changeFeature("reaction")}
            className={`relative isolate flex min-h-[76px] items-center gap-3 overflow-hidden border-r border-[#303238] px-4 text-left transition-colors lg:w-full lg:border-b lg:border-r-0 ${
              activeFeature === "reaction" ? "text-white" : "text-gray-400 hover:bg-[#222328] hover:text-white"
            }`}
          >
            {activeFeature === "reaction" && <motion.span layoutId="partybot-active-feature" className="absolute inset-0 -z-10 bg-[#5865F2]/14 shadow-[inset_3px_0_0_#7c86ff]" transition={indicatorTransition} />}
            <MessageSquareText className={`relative z-10 h-5 w-5 flex-none ${activeFeature === "reaction" ? "text-[#8f97ff]" : "text-gray-500"}`} aria-hidden="true" />
            <span className="relative z-10 min-w-0">
              <span className="block text-sm font-bold">{tx("역할 버튼", "Nút vai trò")}</span>
              <span className="mt-1 hidden truncate text-xs font-normal text-gray-500 sm:block">{tx("이모지로 역할 지급", "Cấp vai trò bằng emoji")}</span>
            </span>
          </button>
          <button
            type="button"
            onClick={() => changeFeature("voice")}
            className={`relative isolate flex min-h-[76px] items-center gap-3 overflow-hidden border-r border-[#303238] px-4 text-left transition-colors lg:w-full lg:border-b lg:border-r-0 ${
              activeFeature === "voice" ? "text-white" : "text-gray-400 hover:bg-[#222328] hover:text-white"
            }`}
          >
            {activeFeature === "voice" && <motion.span layoutId="partybot-active-feature" className="absolute inset-0 -z-10 bg-[#5865F2]/14 shadow-[inset_3px_0_0_#7c86ff]" transition={indicatorTransition} />}
            <Mic2 className={`relative z-10 h-5 w-5 flex-none ${activeFeature === "voice" ? "text-[#8f97ff]" : "text-gray-500"}`} aria-hidden="true" />
            <span className="relative z-10 min-w-0">
              <span className="block text-sm font-bold">{tx("자동 음성방", "Phòng thoại tự động")}</span>
              <span className="mt-1 hidden truncate text-xs font-normal text-gray-500 sm:block">{tx("입장 시 개인방 생성", "Tạo phòng khi tham gia")}</span>
            </span>
          </button>
          <button
            type="button"
            onClick={() => changeFeature("welcome")}
            className={`relative isolate flex min-h-[76px] items-center gap-3 overflow-hidden px-4 text-left transition-colors lg:w-full lg:border-b ${
              activeFeature === "welcome" ? "text-white" : "text-gray-400 hover:bg-[#222328] hover:text-white"
            }`}
          >
            {activeFeature === "welcome" && <motion.span layoutId="partybot-active-feature" className="absolute inset-0 -z-10 bg-[#5865F2]/14 shadow-[inset_3px_0_0_#7c86ff]" transition={indicatorTransition} />}
            <UserRoundPlus className={`relative z-10 h-5 w-5 flex-none ${activeFeature === "welcome" ? "text-[#8f97ff]" : "text-gray-500"}`} aria-hidden="true" />
            <span className="relative z-10 min-w-0">
              <span className="block text-sm font-bold">{tx("입장 & 퇴장", "Chào & tạm biệt")}</span>
              <span className="mt-1 hidden truncate text-xs font-normal text-gray-500 sm:block">{tx("환영 메시지 설정", "Cài tin nhắn chào")}</span>
            </span>
          </button>
        </nav>

        <div className="min-w-0">
          {loadingChannels ? (
            <div className="flex min-h-[430px] items-center justify-center">
              <LoaderCircle className="h-6 w-6 animate-spin text-[#7c86ff]" aria-hidden="true" />
              <span className="ml-3 text-sm text-gray-400">{tx("Discord 정보를 불러오는 중", "Đang tải dữ liệu Discord")}</span>
            </div>
          ) : connectionError ? (
            <div className="flex min-h-[430px] flex-col items-center justify-center px-6 text-center">
              <CircleAlert className="h-9 w-9 text-amber-400" aria-hidden="true" />
              <h3 className="mt-4 text-base font-bold text-white">{tx("파티봇을 연결할 수 없습니다", "Không thể kết nối PartyBot")}</h3>
              <p className="mt-2 max-w-md text-sm leading-6 text-gray-400">{connectionError}</p>
              <a
                href={botInviteLink}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-5 inline-flex items-center gap-2 rounded-lg bg-[#5865F2] px-4 py-2.5 text-sm font-bold text-white transition-colors hover:bg-[#4752c4]"
              >
                {tx("파티봇 초대", "Mời PartyBot")}
                <ExternalLink className="h-4 w-4" aria-hidden="true" />
              </a>
            </div>
          ) : (
            <>
              {notice && (
                <div
                  role="status"
                  className={`mx-5 mt-5 flex items-start gap-2.5 rounded-lg border px-4 py-3 text-sm sm:mx-6 ${
                    notice.type === "success"
                      ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-200"
                      : "border-amber-500/30 bg-amber-500/10 text-amber-100"
                  }`}
                >
                  {notice.type === "success" ? (
                    <Check className="mt-0.5 h-4 w-4 flex-none" aria-hidden="true" />
                  ) : (
                    <CircleAlert className="mt-0.5 h-4 w-4 flex-none" aria-hidden="true" />
                  )}
                  <span>{notice.message}</span>
                </div>
              )}

              <AnimatePresence mode="wait" initial={false}>
                <motion.div key={activeFeature} {...motionProps}>
                  {activeFeature === "reaction" ? (
                  <div className="grid xl:grid-cols-[minmax(0,1fr)_300px]">
                    <div className="space-y-5 p-5 sm:p-6">
                      <div>
                        <h3 className="text-base font-bold text-white">{tx("역할 버튼 만들기", "Tạo nút vai trò")}</h3>
                        <p className="mt-1 text-sm text-gray-500">{tx("완료하면 선택한 Discord 채널에 메시지가 전송됩니다.", "Tin nhắn sẽ được gửi đến kênh Discord đã chọn.")}</p>
                      </div>

                      {reactionConfigs.length > 0 && (
                        <div className="space-y-2 rounded-lg border border-[#34363c] bg-[#202125] p-4">
                          <h4 className="text-sm font-bold text-white">{tx("사용 중인 역할 버튼", "Nút vai trò đang dùng")}</h4>
                          {reactionConfigs.map((config) => (
                            <div key={config.messageId} className="flex items-center justify-between gap-3 rounded-lg border border-[#3a3c43] bg-[#232428] px-3 py-2.5">
                              <div className="min-w-0">
                                <p className="truncate text-sm font-semibold text-gray-200"># {channels.text.find((channel) => channel.id === config.channelId)?.name || config.channelId}</p>
                                <p className="mt-0.5 text-xs text-gray-500">{tx(`${config.itemCount}개 역할`, `${config.itemCount} vai trò`)}</p>
                              </div>
                              <button type="button" onClick={() => handleDeleteReaction(config.messageId)} disabled={savingFeature !== ""} className="flex h-9 w-9 flex-none items-center justify-center rounded-md text-gray-400 transition-colors hover:bg-red-500/10 hover:text-red-300 disabled:opacity-40" aria-label={tx("역할 버튼 삭제", "Xóa nút vai trò")}>
                                {savingFeature === `reaction-delete-${config.messageId}` ? <LoaderCircle className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                              </button>
                            </div>
                          ))}
                        </div>
                      )}

                      <div>
                        <label className="mb-1.5 block text-xs font-semibold text-gray-400">{tx("메시지를 보낼 채널", "Kênh gửi tin nhắn")}</label>
                        <FormDropdown
                          value={reactionForm.channelId}
                          onChange={(channelId) => setReactionForm((current) => ({ ...current, channelId }))}
                          options={reactionChannelOptions}
                          placeholder={tx("텍스트 채널 선택", "Chọn kênh văn bản")}
                          ariaLabel={tx("메시지를 보낼 채널 선택", "Chọn kênh gửi tin nhắn")}
                        />
                      </div>

                      {reactionChannelOptions.length === 0 && (
                        <div className="flex gap-2.5 rounded-lg border border-amber-500/25 bg-amber-500/10 px-4 py-3 text-sm leading-6 text-amber-100">
                          <CircleAlert className="mt-0.5 h-4 w-4 flex-none" aria-hidden="true" />
                          <span>{tx("메시지를 보낼 수 있는 텍스트 채널이 없습니다.", "Không có kênh văn bản để gửi tin nhắn.")}</span>
                        </div>
                      )}

                      {roleOptions.length === 0 && (
                        <div className="flex gap-2.5 rounded-lg border border-amber-500/25 bg-amber-500/10 px-4 py-3 text-sm leading-6 text-amber-100">
                          <ShieldCheck className="mt-0.5 h-4 w-4 flex-none" aria-hidden="true" />
                          <span>{tx("파티봇 역할을 지급할 역할보다 위로 옮긴 뒤 다시 열어 주세요.", "Hãy đặt vai trò PartyBot cao hơn vai trò cần cấp rồi mở lại.")}</span>
                        </div>
                      )}

                      <div className="space-y-3">
                        <div className="flex items-center justify-between gap-3">
                          <div>
                            <h4 className="text-sm font-bold text-white">{tx("이모지별 역할", "Vai trò theo emoji")}</h4>
                            <p className="mt-0.5 text-xs text-gray-500">{tx("한 메시지에 최대 10개까지 설정할 수 있습니다.", "Có thể cài tối đa 10 mục trong một tin nhắn.")}</p>
                          </div>
                          <span className="text-xs font-semibold text-gray-500">{reactionForm.items.length}/10</span>
                        </div>

                        {reactionForm.items.map((item, index) => (
                          <div key={index} className="rounded-lg border border-[#36383f] bg-[#202125] p-4">
                            <div className="mb-3 flex items-center justify-between gap-3">
                              <span className="text-xs font-bold text-gray-400">{tx(`역할 항목 ${index + 1}`, `Mục vai trò ${index + 1}`)}</span>
                              <button
                                type="button"
                                onClick={() => setReactionForm((current) => ({
                                  ...current,
                                  items: current.items.filter((_, itemIndex) => itemIndex !== index),
                                }))}
                                disabled={reactionForm.items.length === 1}
                                className="flex h-8 w-8 items-center justify-center rounded-md text-gray-500 transition-colors hover:bg-red-500/10 hover:text-red-300 disabled:cursor-not-allowed disabled:opacity-30"
                                aria-label={tx(`${index + 1}번 역할 항목 삭제`, `Xóa mục vai trò ${index + 1}`)}
                              >
                                <Trash2 className="h-4 w-4" aria-hidden="true" />
                              </button>
                            </div>

                            <div className="grid gap-4">
                              <div>
                                <label className="mb-1.5 block text-xs font-semibold text-gray-400">{tx("지급할 역할", "Vai trò sẽ cấp")}</label>
                                <FormDropdown
                                  value={item.roleId}
                                  onChange={(roleId) => setReactionForm((current) => ({
                                    ...current,
                                    items: current.items.map((currentItem, itemIndex) => itemIndex === index ? { ...currentItem, roleId } : currentItem),
                                  }))}
                                  options={roleOptions}
                                  placeholder={tx("역할 선택", "Chọn vai trò")}
                                  ariaLabel={tx(`${index + 1}번 지급 역할 선택`, `Chọn vai trò cho mục ${index + 1}`)}
                                />
                              </div>
                              <div>
                                <label className="mb-1.5 block text-xs font-semibold text-gray-400">{tx("버튼 이모지", "Emoji của nút")}</label>
                                <div className="flex flex-wrap gap-2">
                                  {["✅", "🎮", "🔔", "👍"].map((emoji) => (
                                    <button
                                      key={emoji}
                                      type="button"
                                      aria-label={emoji}
                                      aria-pressed={item.emoji === emoji}
                                      onClick={() => setReactionForm((current) => ({
                                        ...current,
                                        items: current.items.map((currentItem, itemIndex) => itemIndex === index ? { ...currentItem, emoji } : currentItem),
                                      }))}
                                      className={`flex h-11 w-11 items-center justify-center rounded-lg border text-lg transition-colors ${
                                        item.emoji === emoji
                                          ? "border-[#7c86ff] bg-[#5865F2]/15"
                                          : "border-[#404249] bg-[#232428] hover:border-[#666a73]"
                                      }`}
                                    >
                                      {emoji}
                                    </button>
                                  ))}
                                  <input
                                    type="text"
                                    value={item.emoji}
                                    onChange={(event) => setReactionForm((current) => ({
                                      ...current,
                                      items: current.items.map((currentItem, itemIndex) => itemIndex === index ? { ...currentItem, emoji: event.target.value } : currentItem),
                                    }))}
                                    className="h-11 w-20 rounded-lg border border-[#404249] bg-[#232428] px-2 text-center text-lg text-white outline-none focus:border-[#5865F2] focus:ring-2 focus:ring-[#5865F2]/25"
                                    aria-label={tx(`${index + 1}번 이모지 직접 입력`, `Nhập emoji cho mục ${index + 1}`)}
                                    maxLength={16}
                                  />
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}

                        <button
                          type="button"
                          onClick={() => setReactionForm((current) => ({
                            ...current,
                            items: [...current.items, { roleId: roleOptions.find((option) => !current.items.some((item) => item.roleId === option.value))?.value || "", emoji: "" }],
                          }))}
                          disabled={reactionForm.items.length >= 10 || roleOptions.length <= reactionForm.items.length}
                          className="inline-flex min-h-10 items-center justify-center gap-2 rounded-lg border border-[#454850] bg-[#25262b] px-4 py-2 text-sm font-bold text-gray-200 transition-colors hover:border-[#6973f5] hover:text-white disabled:cursor-not-allowed disabled:opacity-40"
                        >
                          <Plus className="h-4 w-4" aria-hidden="true" />
                          {tx("역할 항목 추가", "Thêm mục vai trò")}
                        </button>
                      </div>

                      <div>
                        <div className="mb-1.5 flex items-center justify-between gap-3">
                          <label className="text-xs font-semibold text-gray-400">{tx("안내 메시지", "Tin nhắn hướng dẫn")}</label>
                          <span className="text-xs text-gray-600">{reactionForm.message.length}/2000</span>
                        </div>
                        <textarea
                          value={reactionForm.message}
                          onChange={(event) => setReactionForm((current) => ({ ...current, message: event.target.value }))}
                          className={`${textInputClass} min-h-[112px] resize-y`}
                          placeholder={tx("역할을 받는 방법을 적어 주세요.", "Nhập hướng dẫn nhận vai trò.")}
                          maxLength={2000}
                        />
                      </div>

                      <button
                        type="button"
                        onClick={() => handleSave("reaction")}
                        disabled={savingFeature !== "" || roleOptions.length === 0 || reactionChannelOptions.length === 0}
                        className="inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-lg bg-[#5865F2] px-5 py-3 text-sm font-bold text-white transition-colors hover:bg-[#4752c4] disabled:cursor-not-allowed disabled:bg-[#3c3f54] disabled:text-gray-400 sm:w-auto"
                      >
                        {savingFeature === "reaction" && <LoaderCircle className="h-4 w-4 animate-spin" aria-hidden="true" />}
                        {tx("역할 버튼 만들기", "Tạo nút vai trò")}
                      </button>
                    </div>

                    <aside className="border-t border-[#303238] bg-[#18191c] p-5 xl:border-l xl:border-t-0" aria-label={tx("메시지 미리보기", "Xem trước tin nhắn")}>
                      <p className="mb-4 text-xs font-bold text-gray-500">{tx("DISCORD 미리보기", "XEM TRƯỚC DISCORD")}</p>
                      <div className="rounded-lg border border-[#33353b] bg-[#202225] p-4">
                        <div className="flex items-start gap-3">
                          <div className="flex h-9 w-9 flex-none items-center justify-center overflow-hidden rounded-full bg-[#5865F2]/15 p-0.5">
                            <img src="/dideok-assistant.png?v=20260805" alt="" className="h-full w-full object-contain" />
                          </div>
                          <div className="min-w-0">
                            <div className="flex flex-wrap items-center gap-1.5">
                              <span className="text-sm font-bold text-white">PartyBot</span>
                              <span className="rounded bg-[#5865F2] px-1 py-0.5 text-[9px] font-bold text-white">BOT</span>
                            </div>
                            <p className="mt-1 whitespace-pre-wrap break-words text-sm leading-6 text-gray-200">
                              {reactionForm.message || tx("안내 메시지가 여기에 표시됩니다.", "Tin nhắn sẽ hiển thị ở đây.")}
                            </p>
                            <div className="mt-2 flex flex-wrap gap-1.5">
                              {reactionForm.items.map((item, index) => (
                                <span key={index} className="inline-flex min-h-8 min-w-10 items-center justify-center rounded-md border border-[#4c4f56] bg-[#2b2d31] px-2 text-base">
                                  {item.emoji || "?"}
                                </span>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                    </aside>
                  </div>
                ) : activeFeature === "voice" ? (
                  <div className="p-5 sm:p-6">
                    <div className="max-w-3xl space-y-5">
                      <div>
                        <div>
                          <h3 className="text-base font-bold text-white">{tx("자동 음성방 설정", "Cài đặt phòng thoại tự động")}</h3>
                          <p className="mt-1 text-sm text-gray-500">{tx("선택한 채널에 입장하면 사용자 전용 음성방이 만들어집니다.", "Phòng thoại riêng sẽ được tạo khi người dùng tham gia kênh đã chọn.")}</p>
                        </div>
                      </div>

                      <div className="grid gap-4 md:grid-cols-2">
                        <div>
                          <label className="mb-1.5 block text-xs font-semibold text-gray-400">{tx("입장 채널", "Kênh vào")}</label>
                          <FormDropdown
                            value={voiceForm.generatorId}
                            onChange={(generatorId) => setVoiceForm((current) => ({ ...current, generatorId }))}
                            options={voiceChannelOptions}
                            placeholder={tx("음성 채널 선택", "Chọn kênh thoại")}
                            ariaLabel={tx("입장을 감지할 음성 채널 선택", "Chọn kênh thoại để theo dõi")}
                          />
                        </div>
                        <div>
                          <label className="mb-1.5 block text-xs font-semibold text-gray-400">{tx("생성 위치", "Vị trí tạo")}</label>
                          <FormDropdown
                            value={voiceForm.categoryId}
                            onChange={(categoryId) => setVoiceForm((current) => ({ ...current, categoryId }))}
                            options={categoryOptions}
                            placeholder={tx("카테고리 선택", "Chọn danh mục")}
                            ariaLabel={tx("음성방이 생성될 카테고리 선택", "Chọn danh mục tạo phòng thoại")}
                          />
                        </div>
                      </div>

                      <div>
                        <label className="mb-1.5 block text-xs font-semibold text-gray-400">{tx("생성될 채널 이름", "Tên kênh sẽ tạo")}</label>
                        <input
                          type="text"
                          value={voiceForm.namePattern}
                          onChange={(event) => setVoiceForm((current) => ({ ...current, namePattern: event.target.value }))}
                          className={textInputClass}
                          placeholder={tx("{user}님의 파티", "Phòng của {user}")}
                          maxLength={100}
                        />
                        <div className="mt-2 flex flex-wrap gap-2">
                          {[
                            tx("{user}님의 파티", "Phòng của {user}"),
                            tx("{user}의 음성방", "Kênh thoại của {user}"),
                            tx("같이 놀아요 - {user}", "Cùng chơi - {user}"),
                          ].map((pattern) => (
                            <button
                              key={pattern}
                              type="button"
                              onClick={() => setVoiceForm((current) => ({ ...current, namePattern: pattern }))}
                              className="rounded-md border border-[#3a3c42] bg-[#232428] px-3 py-1.5 text-xs text-gray-300 transition-colors hover:border-[#5865F2] hover:text-white"
                            >
                              {pattern}
                            </button>
                          ))}
                        </div>
                      </div>

                      <div className="flex items-center gap-3 rounded-lg border border-[#33353b] bg-[#202125] px-4 py-3">
                        <Mic2 className="h-4 w-4 flex-none text-[#8f97ff]" aria-hidden="true" />
                        <div className="min-w-0">
                          <span className="block text-xs text-gray-500">{tx("채널 이름 미리보기", "Xem trước tên kênh")}</span>
                          <span className="block truncate text-sm font-semibold text-white">
                            {(voiceForm.namePattern || tx("{user}님의 파티", "Phòng của {user}")).replaceAll("{user}", tx("디덕이", "Diduck"))}
                          </span>
                        </div>
                      </div>

                      {voiceChannelOptions.length === 0 && (
                        <div className="flex gap-2.5 rounded-lg border border-amber-500/25 bg-amber-500/10 px-4 py-3 text-sm leading-6 text-amber-100">
                          <CircleAlert className="mt-0.5 h-4 w-4 flex-none" aria-hidden="true" />
                          <span>{tx("먼저 Discord 서버에 음성 채널을 만들어 주세요.", "Hãy tạo kênh thoại trên Discord trước.")}</span>
                        </div>
                      )}

                      <div className="flex flex-col gap-2 sm:flex-row">
                        <button
                          type="button"
                          onClick={() => handleSave("voice")}
                          disabled={savingFeature !== "" || voiceChannelOptions.length === 0}
                          className="inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-lg bg-[#5865F2] px-5 py-3 text-sm font-bold text-white transition-colors hover:bg-[#4752c4] disabled:cursor-not-allowed disabled:bg-[#3c3f54] disabled:text-gray-400 sm:w-auto"
                        >
                          {savingFeature === "voice" && <LoaderCircle className="h-4 w-4 animate-spin" aria-hidden="true" />}
                          {tx("자동 음성방 저장", "Lưu phòng thoại tự động")}
                        </button>
                        {voiceConfigured && (
                          <button
                            type="button"
                            onClick={handleDisableVoice}
                            disabled={savingFeature !== ""}
                            className="inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-lg border border-[#45474e] bg-[#25262a] px-5 py-3 text-sm font-bold text-gray-300 transition-colors hover:border-red-400/50 hover:text-red-300 disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto"
                          >
                            {savingFeature === "voice-disable" && <LoaderCircle className="h-4 w-4 animate-spin" aria-hidden="true" />}
                            {tx("자동 음성방 끄기", "Tắt phòng thoại tự động")}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ) : (
                  <PartyBotWelcomeSettings
                    selectedServerId={selectedServerId}
                    selectedServer={selectedServer}
                    channels={reactionChannelOptions}
                    initialConfig={welcomeConfig}
                    foodOwned={foodOwned}
                    onFoodOwned={() => setFoodOwned(true)}
                    onSaved={setWelcomeConfig}
                    setNotice={setNotice}
                  />
                )}
                </motion.div>
              </AnimatePresence>
            </>
          )}
        </div>
      </div>
    </section>
  );
}
